import * as EventTracker from '@src/analytics/EventTracker';
import { ConfirmModal } from '@src/components/common/ConfirmModal';
import { SPEAKER_FACE_PILE_TOOLTIP_ID } from '@src/components/common/SpeakerFacePile';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { deriveReelSections } from '@src/services/reel/deriveReelSections';
import {
  deriveReelChapterGroups,
  isChapterTitleStale,
} from '@src/services/reel/reelChapters';
import { reelInsertIndexAtTime } from '@src/services/reel/reelInsertIndex';
import {
  CHAPTER_NAME_PROMPT_TITLE,
  chapterBaseText,
} from '@src/services/reel/reelNamingBaseText';
import { reelPlayheadAfterReorder } from '@src/services/reel/reelPlayheadAfterReorder';
import { reelSectionContents } from '@src/services/reel/reelSectionContents';
import { reelSectionTranscriptIndex } from '@src/services/reel/reelSectionTranscriptIndex';
import { reelSectionWords } from '@src/services/reel/reelSectionWords';
import { composeReelSummary } from '@src/services/reel/reelStructureSummary';
import { sectionIdAtTime } from '@src/services/reel/sectionIdAtTime';
import { deriveSectionSourceDelta } from '@src/services/reel/sectionSourceDelta';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { parseClipThemeTitles } from '@src/utility/story';
import { convertTimeToMMSS } from '@src/utility/timeFormat';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { clipFragmentsFromClips } from '../clipFragmentsFromClips';
import { AddClipsPicker } from './AddClipsPicker';
import { AutoChapterOverlay } from './AutoChapterOverlay';
import { InsertDivider } from './InsertDivider';
import { ReelChapterContextMenu } from './ReelChapterContextMenu';
import { ReelChapterRow } from './ReelChapterRow';
import {
  chapterDisplayTitle,
  normalizeReelQuery,
  reelRowMatches,
} from './reelEditorSearch';
import { ReelEditorUtilityBar } from './ReelEditorUtilityBar';
import { ReelNameGenerationDialog } from './ReelNameGenerationDialog';
import { ReelSectionContextMenu } from './ReelSectionContextMenu';
import {
  ReelSectionRow,
  SECTION_ADDED_FLASH_MS,
  SECTION_REVEAL_FLASH_MS,
} from './ReelSectionRow';
import { sourceFrameThumbnailUrl } from './reelThumbnail';
import { usePreloadThumbnails } from './usePreloadThumbnails';
import { useReelChapterization } from './useReelChapterization';
import { useReelEditor } from './useReelEditor';
import { useReelEditorDrag } from './useReelEditorDrag';
import { useReelNameGeneration } from './useReelNameGeneration';
import { useReelSearchMatches } from './useReelSearchMatches';
import { useReelSectionNaming } from './useReelSectionNaming';
import { useSectionSpeakers } from './useSectionSpeakers';
import { useSectionWords } from './useSectionWords';

import type { ClipVideoCreatorAdapter } from '../hooks/ClipVideoCreatorAdapter';
import type {
  AutoChapterListRow,
  AutoChapterRunOptions,
} from './AutoChapterOverlay';
import type { ReelChapterMenuTarget } from './ReelChapterContextMenu';
import type { ReelSectionMenuTarget } from './ReelSectionContextMenu';
import type { TopicSpans } from './storyTopicSpans';
import type { ReelEditorRow } from './useReelEditor';
import type { ReelDropTarget } from './useReelEditorDrag';
import type { ClipPreviewEditorStore } from '@src/editor/ClipPreviewEditorStore';
import type { VideoEditor } from '@src/editor/VideoEditor';
import type { ReelSectionWord } from '@src/services/reel/reelSectionWords';
import type { DatoClientStore } from '@src/stores-v2/DatoClientStore';
import type { VideoSourceStore } from '@src/stores-v2/VideoSourceStore';
import type {
  ClipMetadata,
  DatoSpeaker,
  OtherVideo,
  ReelSectionOrigin,
  Story,
} from '@src/types.ts/story';
import type { TranscriptElement } from '@src/types.ts/transcript';

interface ReelEditorPanelProps {
  editorStore: ClipPreviewEditorStore | null;
  /** Source of the frame previews; omit and the rows show plain placeholders. */
  story?: Story | null;
  /** Resolves speaker labels to people for the face pile. */
  datoClientStore?: DatoClientStore;
  /** Flushes the debounced renderer push after a move, so the canvas keeps up. */
  videoSourceStore?: VideoSourceStore | null;
  /**
   * Owner of the clip's stored state: the reel's names come off its current
   * variant and renames are written back through it (PLAT-11054).
   */
  adapter?: ClipVideoCreatorAdapter | null;
  /** The clip being edited — excluded from its own Add Clips picker. */
  clip?: OtherVideo | null;
  /**
   * Where each of the story's topics is spoken in the source, for the picker's
   * topic filter. Resolved by the clip preview (which owns the transcript and
   * the topic mapping) rather than here: this panel takes its story data as
   * props. Absent stands the topic control down (DS10).
   */
  topicSpans?: TopicSpans;
  /**
   * PLAT-11109 — the source story's transcript, for the scrub readout of the
   * clip being previewed in the Add Clips picker. Resolved by the clip preview
   * alongside `topicSpans` and for the same reason: this panel takes its story
   * data as props. Absent leaves that readout its timecode and no words.
   */
  sourceElements?: readonly TranscriptElement[];
  /**
   * PLAT-11061 — show this Section's words in the transcript. Resolved here
   * because this panel is the side that holds the Sections; its host owns the
   * tab the transcript lives on, and therefore the order the two happen in.
   * Absent when this clip preview has no transcript tab, which stands the menu
   * item down.
   *
   * Both coordinates travel: the token's own id is what the transcript lands
   * on, the index is the fallback for a token that has none.
   */
  onEditSectionTranscript?: (target: {
    index: number;
    tokenId?: string;
  }) => void;
  /**
   * PLAT-11146 — whether the host is showing this panel. It stays mounted when
   * hidden, so its window-bound undo hotkeys read this rather than their own
   * mount.
   */
  isVisible?: boolean;
  /**
   * PLAT-11163 — the transcript's way back: reveal this Section's row once
   * the host has switched to this tab. Consumed once (the host clears it via
   * `onRevealHandled`); a folded chapter opens TRANSIENTLY — nothing is
   * written to the reel by arriving here.
   */
  revealSectionId?: string | null;
  onRevealHandled?: () => void;
}

/**
 * How the Add Clips action names its destination (PLAT-11057 DS1). The
 * playhead has already chosen it, so the button says so rather than leaving the
 * creator to find out after the fact.
 */
function insertDestinationLabel(
  rows: ReelEditorRow[],
  insertIndex: number,
): string {
  if (rows.length === 0 || insertIndex === 0) {
    return 'Adding at the start of the reel';
  }
  if (insertIndex >= rows.length) return 'Adding at the end of the reel';
  return `Adding after “${rows[insertIndex - 1].displayName}”`;
}

/**
 * The people heard in a Section, deduplicated — two labels can resolve to the
 * same person once speakers have been combined. Labels with nobody behind them
 * drop out, so a passage whose speaker was never identified yields an empty
 * list and the row shows no face pile at all.
 */
function identifiedSpeakers(
  labels: string[],
  speakersByLabel: Map<string, DatoSpeaker>,
): DatoSpeaker[] {
  const byId = new Map<string, DatoSpeaker>();
  for (const label of labels) {
    const speaker = speakersByLabel.get(label);
    if (speaker) byId.set(speaker.id, speaker);
  }
  return [...byId.values()];
}

/**
 * One tooltip instance for the panel's own controls, kept apart from the face
 * pile's shared id so the two never fight over an anchor. Modal style — this
 * list lives inside the clip preview, above which a page-level tooltip would
 * disappear.
 */
const REEL_EDITOR_TOOLTIP_ID = 'reel-editor-tooltip';

const logger = LogManager.getLogger('content:ReelEditorPanel');

/**
 * How far *inside* a Section a click on its row lands the playhead.
 *
 * Not on the boundary itself. `setTime` asks the renderer for a time, but what
 * `adapter.time` ends up holding is whatever the player's RAF loop writes back
 * from the renderer — and a renderer that settles a float or a frame short of
 * the request reports a position that still belongs to the Section *above*
 * (`sectionIdAtTime` gives a boundary to the Section starting there). The
 * marking would land on the right row and then jump up one as the seek settled.
 *
 * 10ms is inside the first frame at any rate up to 60fps, so the frame on the
 * canvas is the one the row shows either way.
 */
const SECTION_SEEK_NUDGE_SECONDS = 0.01;

/**
 * PLAT-11063 — how long a query has to stand still before it counts as a search
 * the creator meant. Long enough to swallow typing, short enough that a query
 * they read the results of is still counted before they move on.
 */
const SEARCH_TRACK_SETTLE_MS = 800;

/**
 * PLAT-11073 — how far the playhead is through a Section, 0–1, for the bar along
 * the bottom of its row. Clamped, because `adapter.time` is whatever the
 * player's RAF loop last wrote: a frame either side of a boundary would
 * otherwise overrun the bar it belongs to.
 */
function sectionProgressAt(row: ReelEditorRow, time: number): number {
  if (row.duration <= 0) return 0;
  return Math.min(1, Math.max(0, (time - row.startTime) / row.duration));
}

/** Media times agree to the millisecond; summed element spans drift below that. */
const SAME_MEDIA_TIME = 5e-4;

/**
 * PLAT-11072 — the lineage stored for the passage starting at `mediaStartTime`
 * (PLAT-11067), read on the way out of the reel.
 *
 * `subClips` is re-derived from the timeline on every save, so a Section that is
 * off has no entry there to carry its history — the entry travels with it
 * instead, and `enableReelSection` writes it back. Matched on media start alone,
 * the looser of the two keys `matchStoredSubClips` uses, since a Section whose
 * words were edited no longer has the duration it was saved with.
 */
function storedOriginAt(
  clipJson: ClipMetadata | undefined,
  mediaStartTime: number,
): ReelSectionOrigin[] | undefined {
  return clipJson?.subClips?.find(
    sub => Math.abs(sub.startTime - mediaStartTime) < SAME_MEDIA_TIME,
  )?.origin;
}

/**
 * What a run of Sections adds up to: the distinct voices across it, how many
 * Sections it holds and how long it runs. Two labels resolving to the same
 * person count once; a label nobody has identified still counts as a voice, so
 * the number holds before speaker identification has been done.
 *
 * The reel's own summary and every chapter heading's both come from here, over
 * their own rows — one line, one shape, at two scopes. What the two do not
 * share is the chapter part: the reel says how many chapters it is in, a
 * chapter heading says which of them it is.
 *
 * The shape itself is `composeReelSummary`, shared with the transcript's
 * heading rows (PLAT-11253 DS1): the two tabs describing one reel two ways is
 * exactly what a second implementation here would eventually produce. What
 * stays local is the only thing that differs — the count of Sections that are
 * **off**, which this panel can see and the transcript's structure excludes by
 * construction.
 *
 * PLAT-11072 — counted over the Sections that are **on**, since this describes
 * what the reel plays and how long it runs. The ones that are off are reported
 * separately at the end, so the creator can see that something is set aside
 * without it being folded into the total.
 */
function reelSummary(
  rows: ReelEditorRow[],
  speakersByLabel: Map<string, DatoSpeaker>,
  // The one part that differs by scope, named rather than positional: the reel
  // says how many chapters it is in, a chapter heading says which one it is.
  scope: { chapterCount?: number; chapterOrdinal?: number } = {},
): string {
  const voices = new Set<string>();
  let totalDuration = 0;
  let live = 0;
  let off = 0;
  for (const row of rows) {
    if (!row.enabled) {
      off += 1;
      continue;
    }
    live += 1;
    totalDuration += row.duration;
    for (const label of row.speakerLabels) {
      voices.add(speakersByLabel.get(label)?.id ?? label);
    }
  }

  const summary = composeReelSummary({
    voiceCount: voices.size,
    clipCount: live,
    duration: totalDuration,
    chapterOrdinal: scope.chapterOrdinal,
    chapterCount: scope.chapterCount,
  });
  // The off tail stays last — it is the aside, not part of the shape, which is
  // why it is here rather than in the shared composer.
  return off > 0 ? `${summary} · ${off} off` : summary;
}

/**
 * Whether a gap is the one a drop would land in — matched on what the gap
 * *means* rather than on which zone the pointer happens to be over
 * (PLAT-11075 DS12).
 *
 * A gap has two zones either side of it (the row above's bottom half, the row
 * below's top half) plus its own rule, and all three report the same target. So
 * the position, the chapter and whether it opens that chapter are what identify
 * it — which is also what keeps the two readings of an inter-chapter gap apart,
 * since they differ on the last two even where the index is equal.
 */
function isGapDropTarget(
  target: ReelDropTarget | null,
  index: number,
  chapterId?: string,
  opensChapter?: boolean,
): boolean {
  return (
    !!target &&
    target.index === index &&
    target.chapterId === chapterId &&
    !!target.opensChapter === !!opensChapter
  );
}

/**
 * PLAT-11051 — the Reel Editor: the clip's Sections in playback order.
 *
 * `observer` is what makes DS1 work — the hook reads the store's observable
 * transcript state, so an edit made in the Transcript tab re-derives this list
 * rather than leaving a stale snapshot behind.
 */
export const ReelEditorPanel: React.FC<ReelEditorPanelProps> = observer(
  ({
    editorStore,
    story,
    datoClientStore,
    videoSourceStore,
    adapter,
    clip,
    topicSpans,
    sourceElements,
    onEditSectionTranscript,
    isVisible = false,
    revealSectionId,
    onRevealHandled,
  }) => {
    // Read off the live variant rather than a snapshot: a rename writes there,
    // and `observer` is what turns that write into a repaint of this list.
    const reel = adapter?.currentVideo?.clipJson?.reel;
    // `rows` is the list the creator sees — the Sections that are on and the
    // ones that are off, together. `liveRows` is the reel as it plays. Every
    // read below picks deliberately between the two (PLAT-11072 DS10).
    const { rows, liveRows, isLoading } = useReelEditor(editorStore, reel);
    const speakersByLabel = useSectionSpeakers(story?.id, datoClientStore);

    // The instance-vs-asset nuance, carried by a tooltip instead of UI: a
    // Section that runs shorter than the clip it came from says so on its
    // duration badge. Looked up in the UNFILTERED clip list — a hidden (or
    // self) source must still resolve, unlike the picker's candidates.
    const sourceNoteById = useMemo(() => {
      const notes = new Map<string, string>();
      const clipJson = adapter?.currentVideo?.clipJson;
      if (!clipJson || !story?.otherVideos) return notes;
      const clipsById = new Map(story.otherVideos.map(v => [v.id, v]));
      for (const row of rows) {
        const origin = storedOriginAt(clipJson, row.mediaStartTime);
        const delta = deriveSectionSourceDelta(
          row,
          origin,
          origin?.[0] ? clipsById.get(origin[0].clipId)?.clipJson : undefined,
        );
        if (delta?.isTrimmed) {
          notes.set(
            row.id,
            `Trimmed from “${delta.sourceTitle}” — source ${convertTimeToMMSS(delta.sourceDuration)}`,
          );
        }
      }
      return notes;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, story?.otherVideos, adapter?.currentVideo?.clipJson?.subClips]);

    const chapterCount = reel?.chapters?.length ?? 0;
    const hasChapters = chapterCount > 0;

    // An empty chapter held open past the last Section — what Sections added at
    // the end of the reel are meant to land in (PLAT-11055 DS7).
    const trailingHolder = (reel?.chapters ?? []).find(
      chapter =>
        !chapter.firstSectionId &&
        chapter.afterSectionId === rows[rows.length - 1]?.id,
    );

    // The gaps that already hold an empty chapter, keyed by the Section each
    // one trails (`''` being the top of the reel). One per gap: a second would
    // be indistinguishable from the first, so the rule there offers no insert.
    const holderGaps = useMemo(
      () =>
        new Set(
          (reel?.chapters ?? [])
            .filter(chapter => !chapter.firstSectionId)
            .map(chapter => chapter.afterSectionId ?? ''),
        ),
      [reel?.chapters],
    );

    // Every way the panel moves the playhead goes through here: a row click, a
    // click or drag on the marked row's play tracker, and the drop that carries
    // the playhead's own passage somewhere else (PLAT-11148).
    const seekTo = useCallback(
      (time: number) => {
        // `setTime` reaches the renderer, which rejects when the iframe is not
        // ready to take a seek. Caught rather than left floating: an unhandled
        // rejection here is a click that silently does nothing, with no line in
        // the log to say why (the same guard `navigateToClickedTime` puts on
        // the timeline's own click-to-seek).
        adapter?.setTime(time)?.catch(error => {
          logger.error(
            'Failed to move the playhead in the reel',
            extractErrorInfo(error),
          );
        });
      },
      [adapter],
    );

    // PLAT-11075 — a drop is a position *and* a chapter, and the two halves are
    // independent (DS7): the index may be unchanged while the boundary moves
    // past this Section, or the Section may move inside one chapter with no
    // boundary touched at all.
    const moveSection = useCallback(
      (fromIndex: number, target: ReelDropTarget) => {
        const editor = editorStore?.editor as VideoEditor | undefined;
        const section = rows[fromIndex];
        if (!editor || !section) return;

        // PLAT-11148 — read before the move, like the counts below: the passage
        // the playhead is inside is the one it has to stay inside once the drop
        // has moved things around it.
        const playheadTime = adapter?.time ?? 0;

        // A gap index counts positions in the *pre-move* list, so lifting the
        // dragged row out shifts every gap after it down by one.
        const toIndex =
          target.index > fromIndex ? target.index - 1 : target.index;

        // The order the creator will see once this drop lands — every row, on
        // and off. The chapter and anchor writes below are both read off it,
        // and it is what turns a display position into a timeline one.
        const ordered = [...rows];
        ordered.splice(fromIndex, 1);
        ordered.splice(toIndex, 0, section);

        // PLAT-11063 — both read before the move: afterwards the elements have
        // already travelled and the boundaries have already been rewritten, so
        // neither could be recovered. `reelSectionContents` is the same helper
        // the command's own layout uses, making this the count that actually
        // moves rather than an estimate of it.
        const carriedElementCount =
          section.enabled && toIndex !== fromIndex
            ? reelSectionContents(editor.timeline, section).length
            : 0;
        // Only worth deriving on a reel that has chapters — on one that has
        // none there is no boundary for the drop to cross.
        const chapterIdBefore = hasChapters
          ? deriveReelChapterGroups(rows, reel?.chapters).find(group =>
              group.rows.some(row => row.id === section.id),
            )?.chapter.id
          : undefined;

        // PLAT-11072 DS10 — a Section that is off is not on the timeline, so a
        // drop on one moves nothing there; its new place is the anchor write
        // below and nothing else.
        const moved =
          section.enabled &&
          toIndex !== fromIndex &&
          // The command counts Sections that are *on*, which the display index
          // does not — an off row between two live ones would push the passage
          // a place further down the timeline than the list shows.
          editor.commandMoveReelSection(
            section.id,
            ordered.filter(row => row.enabled).indexOf(section),
          );

        // After the command, so the undo snapshot it took beforehand still
        // holds the anchors as they were (DS8) — and against the pre-move ids,
        // which is where a departing chapter's next Section is read from.
        //
        // Only once the timeline agrees. A boundary write is positional, so a
        // command that refused (an unknown Section, no transcript track) would
        // otherwise leave the boundaries moved with the Sections still where
        // they were — the panel would render a Section under the wrong heading
        // and the debounced save would persist that disagreement. The
        // no-op-index case is the exception DS7 exists for: nothing moved
        // because nothing had to, and the boundary is the whole of the edit.
        if (
          hasChapters &&
          (moved || toIndex === fromIndex || !section.enabled)
        ) {
          adapter?.moveReelSectionChapters(
            rows.map(row => row.id),
            {
              sectionId: section.id,
              chapterId: target.chapterId,
              opensChapter: target.opensChapter,
            },
            // PLAT-11146 — no command ran, so this write is the whole edit and
            // has to carry the undo entry for it. Which is the everyday drag
            // across a boundary: the Section on either side of one is already
            // next to it, so crossing it leaves playback order untouched.
            { ownsUndoEntry: !moved },
          );
        }

        // PLAT-11072 DS10 — and every off row re-reads its anchor off the order
        // the drop produced. Both directions need it: the row that was dragged
        // has a new place to record, and a row left stranded by the live row
        // above it moving away would otherwise jump to wherever its stale
        // anchor now sits. Nothing off, nothing to re-anchor — which is every
        // drag on a reel that has never used the feature.
        if (toIndex !== fromIndex && reel?.disabledSections?.length) {
          adapter?.reanchorDisabledReelSections(ordered.map(row => row.id));
        }

        // PLAT-11063 — a drop the reel took, by any of the three ways it can
        // take one, since a move is a position *and* a chapter and either half
        // alone is an edit (DS7): the timeline moved the passage, the boundary
        // moved past this Section, or an off row — which has no timeline place
        // to change — landed somewhere new and had its anchor rewritten.
        //
        // Tracked above the return below, not after it: that return is about the
        // canvas having nothing to repaint, which is true of exactly the two
        // cases that are still edits. What fires nothing is a drop that came
        // back to its own index inside its own chapter, and one the command
        // refused — neither changed the reel (DS2).
        const crossedChapter =
          hasChapters && target.chapterId !== chapterIdBefore;
        const reanchoredOffRow = !section.enabled && toIndex !== fromIndex;
        if (moved || crossedChapter || reanchoredOffRow) {
          EventTracker.trackReelSectionMoved({
            clip_id: clip?.id,
            from_index: fromIndex,
            to_index: toIndex,
            crossed_chapter: crossedChapter,
            carried_element_count: carriedElementCount,
          });
        }

        // Nothing for the canvas to repaint when only a boundary moved: a
        // Chapter renders nothing on the video (DE14).
        if (!moved) return;

        // PLAT-11148 — follow the passage the playhead was inside: the reorder
        // just gave it a new place in time, and a playhead left where it was
        // would now sit inside whatever moved into its old one — which is what
        // made the marking look like it stayed behind on the row the passage
        // left. `liveRows` is this closure's pre-move copy, so the two orders
        // being compared are the ones either side of the drop.
        const followTime = reelPlayheadAfterReorder(
          liveRows,
          deriveReelSections(editor.timeline),
          playheadTime,
        );

        // The command's TIMELINE_CHANGED notification has already run
        // `refreshTimeline`, which handed the new source to VideoSourceStore —
        // so the rows below are current. What is left is the renderer push,
        // which the store holds behind a 300ms debounce. Flush it: a drop is
        // one discrete edit, not the rapid stream the debounce exists to
        // coalesce. (`refreshSource()` would only re-arm that debounce with the
        // same payload, pushing the canvas a further 300ms out.)
        //
        // After the next frame, so the reordered list paints before the
        // renderer work rather than behind it.
        requestAnimationFrame(() => {
          void videoSourceStore?.flushChanges();
          // After the flush, so the renderer already holds the reordered source
          // when the playhead lands in it rather than seeking the old one.
          if (followTime !== null) seekTo(followTime);
        });
      },
      [
        adapter,
        clip?.id,
        editorStore,
        hasChapters,
        liveRows,
        reel?.chapters,
        reel?.disabledSections,
        rows,
        seekTo,
        videoSourceStore,
      ],
    );

    // PLAT-11060 — one menu for the list, not one per row (DS2). The rows report
    // where a right-click landed and hold no open/closed state of their own, so
    // their `React.memo` short-circuits still hold through a drag.
    const [sectionMenu, setSectionMenu] =
      useState<ReelSectionMenuTarget | null>(null);
    const closeSectionMenu = useCallback(() => setSectionMenu(null), []);
    // PLAT-11064 — the Chapter menu, on the same one-portal-for-the-list pattern
    // (PLAT-11060 DS2). A separate target rather than a widened one: the two
    // menus are different components over different rows.
    const [chapterMenu, setChapterMenu] =
      useState<ReelChapterMenuTarget | null>(null);
    const closeChapterMenu = useCallback(() => setChapterMenu(null), []);
    /**
     * Which Chapter heading was asked to open its title field, from the menu's
     * Edit Name. Held here rather than in the row for the same reason the menu
     * target is: one menu serves the whole list, so the ask arrives with an id
     * and the heading it names is the one that answers it. Cleared by that
     * heading the moment the field closes, committed or not.
     */
    const [renamingChapterId, setRenamingChapterId] = useState<string | null>(
      null,
    );
    const clearRenamingChapterId = useCallback(
      () => setRenamingChapterId(null),
      [],
    );
    /**
     * The same for a Section row, from its menu's Edit Name (PLAT-11397): the
     * rows carry no pencil, so this is the affordance, and it lands the same way
     * a Chapter's does — the panel names the row, the row opens its own field.
     */
    const [renamingSectionId, setRenamingSectionId] = useState<string | null>(
      null,
    );
    const clearRenamingSectionId = useCallback(
      () => setRenamingSectionId(null),
      [],
    );
    // The row the open menu belongs to — which of its items are available turns
    // on whether that Section is on (PLAT-11072 DS7).
    const menuRow = sectionMenu
      ? rows.find(row => row.id === sectionMenu.sectionId)
      : undefined;

    // PLAT-11060 — drops a Section, and everything it owns, out of the reel.
    const removeSection = useCallback(
      (sectionId: string) => {
        const editor = editorStore?.editor as VideoEditor | undefined;
        if (!editor) return;

        // Read off the pre-removal rows: afterwards the Section is gone and
        // there is nothing left to read "the one after it" from. The same
        // ordering `moveSection` gives for its chapter write, in reverse.
        const rowIds = rows.map(row => row.id);

        // PLAT-11072 DS7 — Remove on a row that is already off has no timeline
        // to change and so no command: dropping the stored entry is the whole
        // of it, and there is nothing for the renderer to repaint.
        if (rows.find(row => row.id === sectionId)?.enabled === false) {
          adapter?.removeDisabledReelSection(sectionId);
          if (hasChapters) {
            adapter?.removeReelSectionChapters(rowIds, sectionId);
          }
          // The reel as it plays is unchanged — the row that just left was
          // already off the timeline.
          EventTracker.trackReelSectionRemoved({
            clip_id: clip?.id,
            section_count_after: liveRows.length,
          });
          return;
        }

        // Route through the editor's commandX wrapper, never a raw `timeline.*`
        // mutator (CLAUDE.md).
        if (!editor.commandRemoveReelSection(sectionId)) return;

        // Only once the timeline agrees, and skipped outright on a reel with no
        // chapters — as `moveSection` skips its own.
        if (hasChapters) {
          adapter?.removeReelSectionChapters(rowIds, sectionId);
        }

        EventTracker.trackReelSectionRemoved({
          clip_id: clip?.id,
          section_count_after: liveRows.length - 1,
        });

        // Same reasoning as a drop: one discrete edit, so flush the store's
        // 300ms debounce rather than re-arming it, and after the next frame so
        // the shortened list paints before the renderer work.
        requestAnimationFrame(() => void videoSourceStore?.flushChanges());
      },
      [
        adapter,
        clip?.id,
        editorStore,
        hasChapters,
        liveRows.length,
        rows,
        videoSourceStore,
      ],
    );

    // PLAT-11072 — one item, both directions (DS6). Off is the removal command
    // with everything the passage needs to come back written down first; on is
    // the insert command, rebuilding it from the source media at the position
    // its row already holds in the list.
    const toggleSectionEnabled = useCallback(
      (sectionId: string) => {
        const editor = editorStore?.editor as VideoEditor | undefined;
        const at = rows.findIndex(row => row.id === sectionId);
        const section = rows[at];
        if (!editor || !section) return;

        if (section.enabled) {
          // A reel of zero playing Sections is not a state anything downstream
          // expects (DS5). The menu greys the item out, so this is the
          // handler's own guard rather than the path a click takes.
          if (liveRows.length <= 1) return;

          // Read **before** the removal: afterwards the passage is off the
          // timeline and none of this can be recovered from it.
          const afterSectionId = rows[at - 1]?.id ?? '';
          const origin = storedOriginAt(
            adapter?.currentVideo?.clipJson,
            section.mediaStartTime,
          );

          // Route through the editor's commandX wrapper, never a raw
          // `timeline.*` mutator (CLAUDE.md). Chapters need no write in either
          // direction: the row keeps its place in the list, so a chapter
          // anchored to it still resolves (DS12).
          if (!editor.commandRemoveReelSection(sectionId)) return;
          adapter?.disableReelSection({ section, afterSectionId, origin });
          EventTracker.trackReelSectionToggled({
            clip_id: clip?.id,
            enabled: false,
            section_count_after: liveRows.length - 1,
          });
        } else {
          // The passage is rebuilt from the source media, exactly as the picker
          // builds an added clip — and lands where its row already sits, which
          // in timeline terms is however many rows above it are on (DS10).
          const addedIds = editor.commandInsertReelSections(
            [
              {
                start: section.mediaStartTime,
                end: section.mediaStartTime + section.duration,
              },
            ],
            rows.slice(0, at).filter(row => row.enabled).length,
          );
          const newSectionId = addedIds?.[0];
          if (!newSectionId) return;

          // The insert minted a new provenance id, so the name, the chapter
          // that opened on it and any anchor behind it are re-keyed onto it.
          adapter?.enableReelSection(sectionId, newSectionId);
          // The same flash an added clip gets: the row it lands on is not
          // necessarily the one the creator right-clicked.
          setJustAddedIds(new Set([newSectionId]));
          EventTracker.trackReelSectionToggled({
            clip_id: clip?.id,
            enabled: true,
            section_count_after: liveRows.length + 1,
          });
        }

        // Same reasoning as a drop: one discrete edit, so flush the store's
        // 300ms debounce rather than re-arming it, and after the next frame so
        // the list paints before the renderer work.
        requestAnimationFrame(() => void videoSourceStore?.flushChanges());
      },
      [adapter, clip?.id, editorStore, liveRows, rows, videoSourceStore],
    );

    // PLAT-11061 — hand the host the Section's place in the transcript, which is
    // the only translation between the two views this needs (DE3: both project
    // the same timeline). A Section whose words have all gone is a no-op rather
    // than a jump to the top of the transcript.
    const editSectionTranscript = useCallback(
      (sectionId: string) => {
        if (!editorStore || !onEditSectionTranscript) return;
        const section = rows.find(row => row.id === sectionId);
        if (!section) return;
        const index = reelSectionTranscriptIndex(
          editorStore.editor.timeline,
          editorStore.transcriptElements,
          section.elementIds,
        );
        if (index === undefined) {
          logger.warn('No transcript index for the Section', { sectionId });
          return;
        }
        const token = editorStore.transcriptElements[index];
        onEditSectionTranscript({
          index,
          tokenId: token?.source_uuid ?? token?.id,
        });
      },
      [editorStore, onEditSectionTranscript, rows],
    );

    // PLAT-11073 — the rows follow the playhead; clicking one is that binding
    // in reverse. It lands at the Section's *start*, so the frame under the
    // playhead is the frame the row is showing — nudged just inside it, for the
    // reason SECTION_SEEK_NUDGE_SECONDS gives.
    const selectSection = useCallback(
      (startTime: number) => {
        seekTo(startTime + SECTION_SEEK_NUDGE_SECONDS);
      },
      [seekTo],
    );

    // Play from this Section — or, when it is already the playing one, pause in
    // place. A transport shortcut, not a Section looper: playback starts where
    // the creator is reading and runs on past the Section's end, so context and
    // the play control live on the same row. Resuming the active Section keeps
    // the playhead where it sits (a scrubbed position must hold), which is why
    // this checks the Section under the playhead rather than trusting the row.
    const toggleSectionPlayback = useCallback(
      (row: ReelEditorRow) => {
        if (!adapter) return;
        // The playhead is only ever inside a Section that is on, so this reads
        // the reel as it plays rather than the list as it is shown.
        const isUnderPlayhead =
          sectionIdAtTime(liveRows, adapter.time) === row.id;
        if (isUnderPlayhead && adapter.isPlaying) {
          adapter.renderer?.pause().catch(error => {
            logger.error('Failed to pause the reel', extractErrorInfo(error));
          });
          // Eager, so the row's icon flips with the click; the renderer's own
          // onPause echoes it a beat later (as ClipTranscriptBridge's
          // review-open pause does).
          runInAction(() => {
            adapter.isPlaying = false;
          });
          return;
        }
        // While the Translation Review compact player owns audio, starting the
        // renderer too would run two soundtracks (transcriptEvents precedent).
        if (adapter.suppressRendererPlayback) return;
        const play = () =>
          adapter.renderer?.play().catch(error => {
            logger.error(
              'Failed to start reel playback',
              extractErrorInfo(error),
            );
          });
        if (isUnderPlayhead) {
          play();
        } else {
          adapter
            .setTime(row.startTime + SECTION_SEEK_NUDGE_SECONDS)
            .then(play)
            .catch(error => {
              logger.error(
                'Failed to move the playhead in the reel',
                extractErrorInfo(error),
              );
            });
        }
      },
      [adapter, liveRows],
    );

    // PLAT-11064 — a second instance for the Chapter prompt. Two instances rather
    // than one keyed by kind: each holds the id of the one record it resolves, and
    // a Section and a Chapter are named from different records.
    const {
      generate: generateChapterTitle,
      generatingIds: generatingChapterIds,
      errors: chapterNameErrors,
    } = useReelNameGeneration(
      CHAPTER_NAME_PROMPT_TITLE,
      {
        storyId: story?.id,
        showcaseId: story?.primaryShowcase?.id,
        clipId: clip?.id,
      },
      datoClientStore,
    );

    /**
     * PLAT-11064 DS3 — which Sections each Chapter's title was generated from,
     * for the session only. Staleness is *derived* by comparing this against what
     * the Chapter holds now; persisting a fingerprint would put a
     * cache-invalidation field into `clipJson` for a hint that only matters while
     * the panel is open, and a reopened reel simply does not claim staleness.
     */
    const [titledFrom, setTitledFrom] = useState<Record<string, string[]>>({});

    /**
     * PLAT-11059 DS2 / PLAT-11064 DS5 — which heading the naming dialog is open
     * for, and the name it stands to replace. An in-app dialog rather than
     * `window.confirm`: the browser's is unstyled, prefixed with the host name,
     * blocks the whole tab, and has nowhere to put the creator's steer. Holding
     * the target in state is what makes it declarative — the generation stands
     * down, and the dialog's answer re-enters it.
     */
    const [nameDialogTarget, setNameDialogTarget] = useState<{
      kind: 'section' | 'chapter';
      id: string;
      currentName?: string;
    } | null>(null);

    /**
     * PLAT-11054 / PLAT-11059 — the typed name and the generated one, and the
     * rule that decides when the second may replace the first.
     *
     * The hook, not this panel: the Transcript tab's Section headings offer the
     * same two actions (PLAT-11257), and DE10's "ask before replacing a name
     * the creator typed" is a product rule two surfaces must not re-derive
     * separately. The dialog stays here, since it is shared with Chapters.
     */
    // Read at call time, so a generation started before an edit reads the list
    // as it is when it runs rather than as it was when the callback was made.
    const rowsRef = useRef(rows);
    rowsRef.current = rows;
    const getNamingTimeline = useCallback(
      () => editorStore?.editor.timeline,
      [editorStore],
    );
    const getNameableSection = useCallback(
      (sectionId: string) =>
        rowsRef.current.find(candidate => candidate.id === sectionId),
      [],
    );
    const confirmNameOverUserName = useCallback(
      (target: { id: string; currentName?: string }) =>
        setNameDialogTarget({ kind: 'section', ...target }),
      [],
    );
    const trackSectionRenamed = useCallback(
      (target: { nameSourceBefore: 'user' | 'generated' | 'clip' }) =>
        EventTracker.trackReelSectionRenamed({
          clip_id: clip?.id,
          name_source_before: target.nameSourceBefore,
        }),
      [clip?.id],
    );
    const {
      renameSection,
      generateSectionName,
      generatingIds: generatingNameIds,
      errors: nameErrors,
    } = useReelSectionNaming({
      adapter,
      getTimeline: getNamingTimeline,
      getSection: getNameableSection,
      context: {
        storyId: story?.id,
        showcaseId: story?.primaryShowcase?.id,
        clipId: clip?.id,
      },
      datoClientStore,
      onConfirmOverUserName: confirmNameOverUserName,
      onRenamed: trackSectionRenamed,
    });

    /**
     * PLAT-11064 — name a Chapter from the Sections inside it.
     *
     * Shared by both triggers: the on-creation generation (DS1) and the
     * right-click item (DS5). `confirmOverUserTitle` is what separates them —
     * a generation the creator aimed at this heading may replace a title they
     * typed, once it has asked; one fired by creating a Chapter never may (DE10).
     */
    const generateChapterName = useCallback(
      async (
        chapterId: string,
        options?: {
          confirmOverUserTitle?: boolean;
          userTitleConfirmed?: boolean;
          guidance?: string;
        },
      ) => {
        const timeline = editorStore?.editor.timeline;
        if (!timeline) return;

        const reelNow = adapter?.currentVideo?.clipJson?.reel;
        const chapter = reelNow?.chapters?.find(
          candidate => candidate.id === chapterId,
        );
        if (!chapter) return;

        if (chapter.titleSource === 'user') {
          if (!options?.confirmOverUserTitle) return;
          if (!options.userTitleConfirmed) {
            setNameDialogTarget({
              kind: 'chapter',
              id: chapterId,
              currentName: chapter.title,
            });
            return;
          }
        }

        // The Sections this Chapter holds, in playback order. `liveRows` rather
        // than `rows`: a Section that is off has no elements on the timeline, so
        // there are no words of it to read (PLAT-11072 DS1).
        const sections = deriveReelChapterGroups(
          liveRows,
          reelNow?.chapters ?? [],
        ).find(group => group.chapter.id === chapterId)?.rows;
        if (!sections?.length) {
          // Reachable: every Section under this Chapter is off, so the heading
          // offers an action with nothing to read. Nothing lands on the row, so
          // say why here rather than returning silently.
          logger.debug('No live sections to name this chapter from', {
            chapterId,
          });
          return;
        }

        const title = await generateChapterTitle(
          chapterId,
          chapterBaseText(timeline, sections, reelNow?.sections),
          options?.guidance,
        );
        // Failure leaves the Chapter with the title it had — the ordinal
        // fallback, for one just created (DS4).
        if (!title) return;

        // Read again rather than trusting `chapter`, captured before the
        // request: seconds have passed and the creator may have typed a title
        // into this heading meanwhile. DE10 protects it from a generation aimed
        // elsewhere, and a generation that has been in flight since before the
        // typing is exactly that — but not the title whose replacement was just
        // confirmed, which is still sitting on the heading unchanged.
        const chapterNow =
          adapter?.currentVideo?.clipJson?.reel?.chapters?.find(
            candidate => candidate.id === chapterId,
          );
        if (
          chapterNow?.titleSource === 'user' &&
          chapterNow.title !== chapter.title
        ) {
          logger.debug(
            'Keeping a title typed while a generation was in flight',
            {
              chapterId,
            },
          );
          return;
        }

        adapter?.setGeneratedReelChapterTitle(chapterId, title);
        // What it was named from, so a later change to the Chapter's contents can
        // be told from no change at all (DS3).
        setTitledFrom(previous => ({
          ...previous,
          [chapterId]: sections.map(section => section.id),
        }));
      },
      [adapter, editorStore, generateChapterTitle, liveRows],
    );

    // The dialog's answer: dismiss it first, then run the generation it held
    // back — with the replacement approved, and with whatever the creator asked
    // the name to say.
    const runNameGeneration = useCallback(
      (guidance?: string) => {
        const target = nameDialogTarget;
        setNameDialogTarget(null);
        if (!target) return;
        if (target.kind === 'section') {
          void generateSectionName(target.id, {
            userNameConfirmed: true,
            guidance,
          });
          return;
        }
        void generateChapterName(target.id, {
          confirmOverUserTitle: true,
          userTitleConfirmed: true,
          guidance,
        });
      },
      [generateChapterName, generateSectionName, nameDialogTarget],
    );

    /**
     * PLAT-11064 DS1 — fire-and-forget naming for Chapters that have just been
     * created, including the implicit opening one a first boundary brings with it
     * (PLAT-11055 DS3): it is a Chapter like any other.
     *
     * The ids come from diffing the reel before and after the write — the writes
     * report how many boundaries they opened (PLAT-11063 counts off that) but not
     * which, and the ids are minted inside them.
     */
    const nameNewChapters = useCallback(
      (idsBefore: string[]) => {
        const before = new Set(idsBefore);
        const created = (
          adapter?.currentVideo?.clipJson?.reel?.chapters ?? []
        ).filter(chapter => !before.has(chapter.id));
        // Not awaited: the Chapter is already on screen and the creator can carry
        // on dragging Sections while it names itself. Blocking the grouping on a
        // round trip would make it feel expensive, which defeats the point.
        created.forEach(chapter => {
          void generateChapterName(chapter.id);
        });
      },
      [adapter, generateChapterName],
    );

    /**
     * PLAT-11099 — chapter the whole reel in one action.
     *
     * The hook owns the request and its state; what belongs here is the two
     * things it deliberately does not own: which Sections the run reads, and
     * where the result lands.
     */
    const [isAutoChapterOpen, setIsAutoChapterOpen] = useState(false);
    const {
      chapterize,
      isRunning: isChapterizing,
      error: chapterizeError,
      reportFailure: reportChapterizeFailure,
      clearError: clearChapterizeError,
    } = useReelChapterization(
      {
        storyId: story?.id,
        showcaseId: story?.primaryShowcase?.id,
        clipId: clip?.id,
      },
      datoClientStore,
    );

    /**
     * PLAT-11154 — the reel as the overlay's tick list, live Sections in
     * playback order with their chapter headings between them. Built here
     * because the panel already derives both; the overlay renders what it is
     * given and owns only which boxes are ticked.
     */
    const autoChapterRows = useMemo((): AutoChapterListRow[] => {
      const chapters = reel?.chapters ?? [];
      const opensAt = new Map<string, string>();
      for (const chapter of chapters) {
        if (chapter.firstSectionId && !opensAt.has(chapter.firstSectionId)) {
          opensAt.set(chapter.firstSectionId, chapter.title);
        }
      }

      let ordinal = 0;
      return liveRows.flatMap((row, index): AutoChapterListRow[] => {
        const section: AutoChapterListRow = {
          kind: 'section',
          id: row.id,
          position: index + 1,
          name: row.displayName,
        };
        const title = opensAt.get(row.id);
        if (title === undefined) return [section];

        ordinal += 1;
        return [
          {
            kind: 'chapter',
            id: `chapter-${row.id}`,
            // An untitled chapter renders as its ordinal everywhere else in the
            // panel (PLAT-11055 DS5); the list says the same thing.
            title: title || `Chapter ${ordinal}`,
          },
          section,
        ];
      });
    }, [liveRows, reel?.chapters]);

    const runChapterization = useCallback(
      async (options: AutoChapterRunOptions) => {
        // Cast as every other reel-command site in this panel does: the store
        // types its editor as the interface, and the reel commands live on the
        // concrete class.
        const editor = editorStore?.editor as VideoEditor | undefined;
        const timeline = editor?.timeline;
        if (!editor || !timeline) return;

        EventTracker.trackReelChapterizationStarted({
          clip_id: clip?.id,
          section_count: liveRows.length,
          chapter_count: options.chapterCount,
          reordered: !!options.reorder,
          has_guidance: !!options.guidance,
          sections_selected: options.movableSectionIds?.size ?? liveRows.length,
          sections_total: liveRows.length,
        });

        // `liveRows` rather than `rows`: a Section that is off has no elements
        // on the timeline, so there is nothing of it to read and it is not in
        // the conversation (PLAT-11153 S7). It keeps its place in the reel
        // regardless — membership is derived from position (DE12).
        const { movableSectionIds, ...promptOptions } = options;
        const currentChapters =
          adapter?.currentVideo?.clipJson?.reel?.chapters ?? [];

        const result = await chapterize({
          timeline,
          sections: liveRows,
          names: adapter?.currentVideo?.clipJson?.reel?.sections,
          chapters: currentChapters,
          movableSectionIds,
          options: promptOptions,
        });
        // A failure leaves the reel exactly as it was, and the overlay stays
        // open holding the reason so a retry is one click away (DS10).
        if (!result) {
          EventTracker.trackReelChapterizationFailed({
            clip_id: clip?.id,
            section_count: liveRows.length,
          });
          return;
        }

        // PLAT-11153 S4 — order first, then chapters, and never the other way
        // round. A chapter is stored as the Section it opens (DE12), so a
        // boundary written against the old order anchors to a Section that is
        // about to move somewhere else.
        if (options.reorder) {
          const isChanged = result.orderedSectionIds.some(
            (id, index) => id !== liveRows[index]?.id,
          );
          // The command refuses an order that changes nothing, so only ask it
          // for one that does — otherwise a `false` here would be read as the
          // failure S8 stops on.
          if (isChanged) {
            const reordered = editor.commandReorderReelSections(
              result.orderedSectionIds,
            );
            // S8 — the two halves are sequenced, so a failure between them is
            // representable and must not be: a reel in its old order carrying
            // the new order's chapters is exactly the half-applied state E9
            // rules out. The command's own snapshot restores the timeline.
            if (!reordered) {
              // Said out loud rather than left as a dialog that did nothing:
              // the request succeeded, so the hook has no error of its own to
              // show, and this is the one failure path the creator would
              // otherwise read as the button being broken.
              reportChapterizeFailure(
                'The reel could not be rearranged, so no chapters were written.',
              );
              EventTracker.trackReelChapterizationFailed({
                clip_id: clip?.id,
                section_count: liveRows.length,
              });
              return;
            }
            // One discrete edit, so flush the store's 300ms debounce rather
            // than re-arming it — the same follow-up every reel move makes.
            void videoSourceStore?.flushChanges();
          }
        }

        // PLAT-11154 S6 — a title this run carried over keeps the provenance it
        // had, so a name the creator typed stays protected from the next
        // generation (DE10). Reported by the constraint pass, which is the only
        // place that knows which titles it carried; a whole-reel run has nothing
        // to carry over, and says so by not reporting any.
        adapter?.replaceReelChapters(
          result.boundaries,
          result.preservedTitleSources,
        );
        EventTracker.trackReelChapterizationCompleted({
          clip_id: clip?.id,
          section_count: liveRows.length,
          chapter_count: result.boundaries.length,
          reordered: !!options.reorder,
          sections_selected: movableSectionIds?.size ?? liveRows.length,
          sections_total: liveRows.length,
        });
        setIsAutoChapterOpen(false);
      },
      [
        adapter,
        chapterize,
        clip?.id,
        editorStore,
        liveRows,
        reportChapterizeFailure,
        videoSourceStore,
      ],
    );

    const closeAutoChapter = useCallback(() => {
      setIsAutoChapterOpen(false);
      // The next open starts clean: a failure from the last run standing over a
      // fresh dialog reads as a failure of the run about to happen.
      clearChapterizeError();
    }, [clearChapterizeError]);

    // PLAT-11055 — chapters are the same kind of write: `clipJson.reel`, not
    // the timeline. The reel's opening Section rides along with the insert
    // because the first boundary below the top has to claim it too (DS3).
    const insertChapter = useCallback(
      (sectionId: string, trigger: 'divider' | 'playhead') => {
        const firstSectionId = rows[0]?.id;
        if (!firstSectionId) return;
        // PLAT-11063 — the write reports how many boundaries it opened: none
        // when one already starts here, so nothing was declared and counting it
        // would report a chapter that does not exist (DS2). Asking the write
        // rather than re-deriving `insertChapterInReel`'s rules here keeps the
        // count from drifting away from the rule it is counting.
        const idsBefore = (
          adapter?.currentVideo?.clipJson?.reel?.chapters ?? []
        ).map(chapter => chapter.id);
        const opened = adapter?.insertReelChapter(sectionId, firstSectionId);
        if (!opened) return;

        // PLAT-11064 — every boundary this opened names itself (DS1).
        nameNewChapters(idsBefore);

        EventTracker.trackReelChapterCreated({
          clip_id: clip?.id,
          section_count: liveRows.length,
          was_implicit: false,
          trigger,
        });
        // The second boundary is the opening chapter DS3 adds on the creator's
        // behalf. Counted separately — without the distinction, first use reads
        // as two deliberate chapters.
        if (opened > 1) {
          EventTracker.trackReelChapterCreated({
            clip_id: clip?.id,
            section_count: liveRows.length,
            was_implicit: true,
            trigger,
          });
        }
      },
      [adapter, clip?.id, liveRows.length, nameNewChapters, rows],
    );

    // Past the last Section, so it owns nothing yet — a holder to add into. It
    // anchors to the Section it trails, which is how Sections landing there
    // later fall into it (DS7).
    const appendChapter = useCallback(
      (afterSectionId: string) => {
        const firstSectionId = rows[0]?.id;
        if (!firstSectionId) return;
        // Counted the same way as `insertChapter`: off what the write opened,
        // which is none when a chapter already trails this Section.
        const idsBefore = (
          adapter?.currentVideo?.clipJson?.reel?.chapters ?? []
        ).map(chapter => chapter.id);
        const opened = adapter?.appendReelChapter(
          afterSectionId,
          firstSectionId,
        );
        if (!opened) return;

        // The trailing holder owns no Sections yet, so there is nothing to read
        // and its own generation will decline — but the opening chapter that
        // comes with the first one does have them, and names itself (DS1).
        nameNewChapters(idsBefore);

        EventTracker.trackReelChapterCreated({
          clip_id: clip?.id,
          section_count: liveRows.length,
          was_implicit: false,
          trigger: 'divider',
        });
        // A chapter past the last Section owns none of them, so the first one
        // always brings the opening chapter with it.
        if (opened > 1) {
          EventTracker.trackReelChapterCreated({
            clip_id: clip?.id,
            section_count: liveRows.length,
            was_implicit: true,
            trigger: 'divider',
          });
        }
      },
      [adapter, clip?.id, liveRows.length, nameNewChapters, rows],
    );

    const renameChapter = useCallback(
      (chapterId: string, title: string) => {
        EventTracker.trackReelChapterRenamed({
          clip_id: clip?.id,
          title_source_before:
            reel?.chapters?.find(c => c.id === chapterId)?.titleSource ??
            'clip',
        });
        adapter?.renameReelChapter(chapterId, title);
        // A title typed by hand is not a stale generated one (DS3).
        setTitledFrom(previous => {
          if (!previous[chapterId]) return previous;
          const next = { ...previous };
          delete next[chapterId];
          return next;
        });
      },
      [adapter, clip?.id, reel?.chapters],
    );

    const removeChapter = useCallback(
      (chapterId: string) => {
        adapter?.removeReelChapter(chapterId);
      },
      [adapter],
    );

    // PLAT-11203 — the chapter headings, off the list and back on. A view state
    // on the open panel rather than a property of the reel (DS3): the reel
    // travels into publishing and into Creator Studio, and one creator's reading
    // mode is not a property of the piece. The chapter *layout* is derived all
    // the while (DS1) — only the rendering changes.
    const [areChaptersHidden, setAreChaptersHidden] = useState(false);

    // PLAT-11204 — the confirm behind Remove All Chapters (DS2). Chapters are a
    // `clipJson.reel` write, outside the timeline history (DE13), so Cmd-Z does
    // not bring them back.
    const [isRemoveAllOpen, setIsRemoveAllOpen] = useState(false);

    // PLAT-11056 — folding a chapter away is stored on it, so it is still
    // folded on the next visit (DS3). The same write as every other chapter
    // edit: the reel, not the timeline.
    const toggleChapterCollapsed = useCallback(
      (chapterId: string, collapsed: boolean) => {
        adapter?.setReelChapterCollapsed(chapterId, collapsed);
        EventTracker.trackReelChapterCollapsed({
          clip_id: clip?.id,
          collapsed,
        });
      },
      [adapter, clip?.id],
    );

    // PLAT-11203 — nothing is written here (DS1): the toggle decides which of
    // the two list renderings runs, and the reel is untouched either way.
    const toggleChaptersHidden = useCallback(() => {
      // Read and tracked outside the updater: a state updater runs twice under
      // StrictMode, which would double-count the toggle.
      const hidden = !areChaptersHidden;
      setAreChaptersHidden(hidden);
      EventTracker.trackReelChaptersHidden({
        clip_id: clip?.id,
        hidden,
        chapter_count: chapterCount,
      });
    }, [areChaptersHidden, chapterCount, clip?.id]);

    // PLAT-11204 — every chapter, in one write (DS1). The Sections, their order
    // and their names are untouched; what goes is the structure over them.
    const removeAllChapters = useCallback(() => {
      adapter?.removeAllReelChapters();
      setIsRemoveAllOpen(false);
      // The headings are gone, so the view mode that hid them has nothing left
      // to hide — and the next chapter created should be on screen.
      setAreChaptersHidden(false);
      // PLAT-11064 DS3's map is keyed by chapter id and describes chapters that
      // no longer exist (DS4).
      setTitledFrom({});
      EventTracker.trackReelChaptersRemovedAll({
        clip_id: clip?.id,
        chapter_count: chapterCount,
        section_count: liveRows.length,
      });
    }, [adapter, chapterCount, clip?.id, liveRows.length]);

    // PLAT-11057 — the playhead is the insertion point (DS1). Read live off the
    // adapter's observable time, so the destination the button names is the one
    // the creator can see on the scrubber.
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const time = adapter?.time ?? 0;
    const playheadIndex = reelInsertIndexAtTime(rows, time);
    const playheadDestination = insertDestinationLabel(rows, playheadIndex);

    // PLAT-11068 — the gap the picker was opened from, and the chapter that gap
    // opens when it is one (DS4). `null` means the utility bar opened it, where
    // the playhead decides (PLAT-11057 DS1). Frozen at the click: the creator
    // pointed at that gap, so a playhead drifting under the open dialog must not
    // move their target (DS2).
    const [pickerGap, setPickerGap] = useState<{
      index: number;
      opensChapterId?: string;
    } | null>(null);

    // PLAT-11071 DS6 — the Section a replace was opened on. Frozen at the
    // right-click for the same reason `pickerGap` is: the picker is a modal over
    // a list that keeps re-deriving (a transcript edit in the other tab, a
    // playhead tick), so the swap remembers which Section the creator aimed at
    // rather than re-reading "the current one" on confirm.
    const [pickerSectionId, setPickerSectionId] = useState<string | null>(null);

    // Sections the picker just put into the reel: their rows flash the hover
    // gray so the creator sees where they landed (the clip grid's
    // recently-saved highlight, scaled to this panel). Cleared once the flash
    // has run so the rows return to rest.
    // The flash carries its duration: an add flashes 2s, a landing from the
    // transcript 4s (the creator's eyes have to cross a tab flip first).
    const [justAdded, setJustAdded] = useState<{
      ids: Set<string>;
      ms: number;
      tone: 'added' | 'reveal';
    } | null>(null);
    const setJustAddedIds = useCallback(
      (
        ids: Set<string> | null,
        ms: number = SECTION_ADDED_FLASH_MS,
        tone: 'added' | 'reveal' = 'added',
      ) => setJustAdded(ids ? { ids, ms, tone } : null),
      [],
    );
    const listRef = useRef<HTMLDivElement>(null);

    // PLAT-11163 — a chapter held open for a reveal, without writing the
    // reel's own collapsed flag (the same transient shape as the search
    // filter's open-everything). Cleared by the next reveal; the creator's
    // stored fold is untouched throughout.
    const [transientOpenChapterId, setTransientOpenChapterId] = useState<
      string | null
    >(null);
    // The hold lasts as long as the creator is looking at the row it was for.
    // Leaving the tab ends it, so a chapter the creator folded is folded again
    // when they come back — arriving at a row is not a decision to unfold its
    // chapter for the rest of the session.
    useEffect(() => {
      if (!isVisible) setTransientOpenChapterId(null);
    }, [isVisible]);
    useLayoutEffect(() => {
      if (!isVisible || !revealSectionId) return;
      const owningGroup = chapterGroups.find(group =>
        group.rows.some(row => row.id === revealSectionId),
      );
      setTransientOpenChapterId(
        owningGroup?.chapter.collapsed ? owningGroup.chapter.id : null,
      );
      // The words are what the creator was just reading when they left the
      // transcript, so the row arrives with its fold open — the same state the
      // chevron's click produces, collapsible from there. Without it the trip
      // lands on a one-line row and the passage has to be opened by hand.
      setManualExpandedIds(prev => new Set(prev).add(revealSectionId));
      // Arriving from the transcript reads like arriving from Add Clips: the
      // same 2s flash marks the row, so the tab flip lands somewhere visible.
      setJustAddedIds(
        new Set([revealSectionId]),
        SECTION_REVEAL_FLASH_MS,
        'reveal',
      );
      // The row may need the expansion (or the tab flip) to commit before it
      // has a box — a few frames of retry, the jump hook's own pattern.
      const attempt = (attemptsLeft: number) => {
        const row = listRef.current?.querySelector(
          `[data-section-id="${revealSectionId}"]`,
        );
        if (row) {
          row.scrollIntoView?.({ block: 'center' });
          return;
        }
        if (attemptsLeft > 0) {
          requestAnimationFrame(() => attempt(attemptsLeft - 1));
        }
      };
      requestAnimationFrame(() => attempt(5));
      onRevealHandled?.();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, revealSectionId]);
    useEffect(() => {
      if (!justAdded) return;
      // An insert at the reel's start or end can land outside the scrolled
      // list, where a flash would run unseen. jsdom has no scrollIntoView,
      // hence the optional call.
      listRef.current
        ?.querySelector('[data-just-added]')
        ?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
      const timer = window.setTimeout(
        () => setJustAddedIds(null),
        justAdded.ms + 400,
      );
      return () => window.clearTimeout(timer);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [justAdded]);

    const insertIndex = pickerGap?.index ?? playheadIndex;
    const replacedRow = pickerSectionId
      ? rows.find(row => row.id === pickerSectionId)
      : undefined;
    const destination = replacedRow
      ? `Replacing “${replacedRow.displayName}”`
      : pickerGap
        ? insertDestinationLabel(rows, pickerGap.index)
        : playheadDestination;

    const openPickerAtGap = useCallback(
      (index: number, opensChapterId?: string) => {
        setPickerSectionId(null);
        setPickerGap({ index, opensChapterId });
        setIsPickerOpen(true);
      },
      [],
    );

    const closePicker = useCallback(() => {
      setIsPickerOpen(false);
      setPickerGap(null);
      setPickerSectionId(null);
    }, []);

    const openReplacePicker = useCallback((sectionId: string) => {
      setPickerGap(null);
      setPickerSectionId(sectionId);
      setIsPickerOpen(true);
    }, []);

    // PLAT-11073 — and the same playhead answers "which Section am I hearing",
    // off the same read. Presentation only: nothing is stored and nothing here
    // can change the reel (DE4).
    //
    // The rows are `React.memo`, so a tick that stays inside one Section
    // repaints nothing and a tick that crosses a boundary repaints the two rows
    // whose marking changed — the list does not repaint per frame.
    const activeSectionId = sectionIdAtTime(liveRows, time);

    // PLAT-11098 — and the same playhead answers "where would a chapter start",
    // which is that Section itself rather than the gap after it: a boundary
    // *opens* a Section, so "a chapter starts here" is the passage being heard
    // (DS1). Deliberately not `playheadIndex`, which Add Clip uses to land
    // *after* the Section because a passage is never split to make room.
    const activeRow = rows.find(row => row.id === activeSectionId);

    // What that Section says, for the sub-line's karaoke and its fold. The
    // marked Section is always walked: it is the row a read-along runs on.
    const activeWords = useSectionWords(editorStore, activeRow?.elementIds);

    // PLAT-11246 rework — which rows have their transcript folded open.
    // Expansion is deliberate, never a hover: the chevron's click, plus the
    // row being played, which the panel opens the way the active player
    // expands itself. The two are held apart so a manual collapse of the
    // playing row sticks until the playhead moves to another Section, and a
    // manually opened row stays open while playback passes through others.
    const [manualExpandedIds, setManualExpandedIds] = useState<Set<string>>(
      () => new Set(),
    );
    const [autoExpandedId, setAutoExpandedId] = useState<string | null>(null);

    useEffect(() => {
      // Entering a Section opens its read-along; leaving closes it — one
      // reflow per boundary, the player pattern.
      setAutoExpandedId(activeSectionId ?? null);
    }, [activeSectionId]);

    const toggleSectionExpanded = useCallback(
      (sectionId: string) => {
        const isExpanded =
          manualExpandedIds.has(sectionId) || autoExpandedId === sectionId;
        if (isExpanded) {
          setAutoExpandedId(current =>
            current === sectionId ? null : current,
          );
          setManualExpandedIds(prev => {
            const next = new Set(prev);
            next.delete(sectionId);
            return next;
          });
        } else {
          setManualExpandedIds(prev => new Set(prev).add(sectionId));
        }
      },
      [manualExpandedIds, autoExpandedId],
    );

    const expandedSectionIds = useMemo(() => {
      const ids = new Set(manualExpandedIds);
      if (autoExpandedId) ids.add(autoExpandedId);
      return ids;
    }, [manualExpandedIds, autoExpandedId]);

    // The words of every expanded Section, walked in one pass off the same
    // service `useSectionWords` wraps — a hook can serve one Section, and any
    // number can be open at once. Re-walked when the transcript signal
    // changes, exactly like the hook.
    const transcriptElements = editorStore?.transcriptElements;
    const wordsBySection = useMemo(() => {
      const map = new Map<string, ReelSectionWord[]>();
      if (!editorStore) return map;
      for (const id of expandedSectionIds) {
        const elementIds = rows.find(row => row.id === id)?.elementIds;
        if (elementIds?.length) {
          map.set(
            id,
            reelSectionWords(editorStore.editor.timeline, elementIds),
          );
        }
      }
      return map;
      // `transcriptElements` is the change signal rather than an input — the
      // words are read off the timeline.
    }, [editorStore, expandedSectionIds, rows, transcriptElements]);

    // A word in a fold was clicked: an ask to *hear* it. A paused player
    // seeks there and starts — the toggle's own seek-then-play order — where
    // one already playing just lands there. Suppressed playback (the review's
    // compact player owns audio) keeps the click a plain seek.
    const seekWordAndPlay = useCallback(
      (time: number) => {
        if (
          adapter &&
          !adapter.isPlaying &&
          !adapter.suppressRendererPlayback
        ) {
          adapter
            .setTime(time)
            ?.then(() =>
              adapter.renderer?.play().catch(error => {
                logger.error(
                  'Failed to start reel playback',
                  extractErrorInfo(error),
                );
              }),
            )
            .catch(error => {
              logger.error(
                'Failed to move the playhead in the reel',
                extractErrorInfo(error),
              );
            });
          return;
        }
        seekTo(time);
      },
      [adapter, seekTo],
    );

    // PLAT-11096 — the story's own topics, for the picker's topic filter. Parsed
    // from the raw field it is stored in, memoized on it: the picker mounts and
    // unmounts on every open, and this does not change while it is open.
    const storyTopics = useMemo(
      () => parseClipThemeTitles(story?.clipThemesJson),
      [story?.clipThemesJson],
    );

    // …and only offered once we know where those topics are *spoken*, which is
    // the only thing a clip can be matched against (`storyTopicSpans.ts`).
    // Without that the control could answer nothing but "no clips match", so it
    // stands down instead (DS10).
    const topicsForFilter = topicSpans?.size ? storyTopics : [];

    // The story's other clips: the picker never offers the reel it would be
    // adding to, nor the hidden clips the grid itself filters out (DE16).
    const candidateClips = useMemo(
      () =>
        (story?.otherVideos ?? []).filter(
          video => !video.isHidden && video.id && video.id !== clip?.id,
        ),
      [story?.otherVideos, clip?.id],
    );

    const addClips = useCallback(
      (picked: OtherVideo[]) => {
        closePicker();

        const editor = editorStore?.editor as VideoEditor | undefined;
        const fragments = clipFragmentsFromClips(picked);
        if (!editor || fragments.length === 0) return;

        const sectionIds = editor.commandInsertReelSections(
          fragments.map(fragment => ({
            start: fragment.startTime,
            end: fragment.startTime + fragment.duration,
          })),
          // `insertIndex` is a gap in the list the creator pointed at; the
          // command counts Sections that are *on* (PLAT-11072 DS10).
          rows.slice(0, insertIndex).filter(row => row.enabled).length,
        );
        if (!sectionIds) return;

        // Names live in `clipJson.reel`, not the timeline (DE13), so they are
        // written separately from the command. Chapters mostly need nothing: an
        // inserted Section joins the chapter it landed in by position, and an
        // insert at the front is adopted by the opening chapter when the groups
        // are derived (PLAT-11055 DS3).
        adapter?.recordInsertedReelSections(
          sectionIds.map((sectionId, index) => ({
            sectionId,
            title: fragments[index]?.title,
          })),
        );

        // The exception is a reel that ends in an empty chapter, which is what
        // one is opened for (PLAT-11055 DS7). An empty chapter owns nothing
        // until something is put into it (PLAT-11075 DS3) — otherwise a chapter
        // emptied by a drag would silently swallow the next Sections added —
        // so an insert at the end anchors it rather than leaving the new
        // Sections to the chapter above.
        if (trailingHolder && insertIndex >= rows.length && sectionIds[0]) {
          adapter?.moveReelSectionChapters(
            rows.map(row => row.id),
            {
              sectionId: sectionIds[0],
              chapterId: trailingHolder.id,
              opensChapter: true,
            },
          );
        }

        // PLAT-11068 DS4 — the same write for the same reason, one gap over: a
        // chapter's boundary shows up twice in the list, and the rule *below* its
        // heading is the one an insert has to move it onto. Left alone the new
        // Section would slide under the boundary and render above the heading the
        // creator clicked beneath it. The row ids are the pre-insert order, where
        // `moveSectionInReelChapters` reads a departure from — the new Section is
        // not among them, so only its arrival half applies. It follows the names
        // write because both are read-modify-writes of the current variant, and
        // `updateVariant` is synchronous: this one sees `isReel` and the names.
        if (pickerGap?.opensChapterId && sectionIds[0]) {
          adapter?.moveReelSectionChapters(
            rows.map(row => row.id),
            {
              sectionId: sectionIds[0],
              chapterId: pickerGap.opensChapterId,
              opensChapter: true,
            },
          );
        }

        // Same reasoning as a drop: one discrete edit, so flush the store's
        // 300ms debounce rather than re-arming it, and after the next frame so
        // the list paints before the renderer work.
        requestAnimationFrame(() => void videoSourceStore?.flushChanges());

        setJustAddedIds(new Set(sectionIds));

        // PLAT-11063 — which of the two ways in the creator used. The same
        // picker, but a gap says they were composing at a specific seam
        // (PLAT-11068) where the utility bar just adds at the playhead
        // (PLAT-11057).
        EventTracker.trackReelClipsAdded({
          clip_id: clip?.id,
          clips_added: sectionIds.length,
          insert_position: pickerGap ? 'gap' : 'playhead',
          section_count_after: liveRows.length + sectionIds.length,
        });
      },
      [
        adapter,
        clip?.id,
        closePicker,
        editorStore,
        insertIndex,
        liveRows.length,
        pickerGap,
        rows,
        trailingHolder,
        videoSourceStore,
      ],
    );

    // PLAT-11071 — the swap: one Section out, the picked clip in, at the same
    // position and in the same chapter. The whole of it is one undo entry, which
    // the command owns (DS1).
    const replaceSection = useCallback(
      (picked: OtherVideo[]) => {
        const sectionId = pickerSectionId;
        closePicker();

        const editor = editorStore?.editor as VideoEditor | undefined;
        // Every passage the picked clip holds, not only its first: an incoming
        // reel is one fragment per passage (DE15).
        const fragments = clipFragmentsFromClips(picked);
        if (!editor || !sectionId || fragments.length === 0) return;

        // Route through the editor's commandX wrapper, never a raw `timeline.*`
        // mutator (CLAUDE.md).
        const addedSectionIds = editor.commandReplaceReelSection(
          sectionId,
          fragments.map(fragment => ({
            start: fragment.startTime,
            end: fragment.startTime + fragment.duration,
          })),
        );
        if (!addedSectionIds?.length) return;

        // Names and the chapter anchor live in `clipJson.reel`, not the timeline
        // (DE13), so they are written separately from the command — and only
        // once it has agreed, since the anchor is positional.
        adapter?.replaceReelSection({
          removedSectionId: sectionId,
          added: addedSectionIds.map((addedId, index) => ({
            sectionId: addedId,
            title: fragments[index]?.title,
          })),
        });

        // Same reasoning as a drop: one discrete edit, so flush the store's
        // 300ms debounce rather than re-arming it, and after the next frame so
        // the list paints before the renderer work.
        requestAnimationFrame(() => void videoSourceStore?.flushChanges());

        setJustAddedIds(new Set(addedSectionIds));

        // PLAT-11063 — `sections_added` is not always 1: an incoming reel opens
        // one Section per passage (DE15).
        EventTracker.trackReelSectionReplaced({
          clip_id: clip?.id,
          index: rows.findIndex(row => row.id === sectionId),
          sections_added: addedSectionIds.length,
        });
      },
      [
        adapter,
        clip?.id,
        closePicker,
        editorStore,
        pickerSectionId,
        rows,
        videoSourceStore,
      ],
    );

    // Per-row props, held stable so a drag — which re-renders this panel on
    // every pointer move — repaints only the rows whose drag state changed,
    // not all twenty with freshly allocated speaker arrays.
    const rowViews = useMemo(
      () =>
        rows.map(row => ({
          id: row.id,
          row,
          thumbnailUrl: sourceFrameThumbnailUrl(story, row.mediaStartTime),
          speakers: identifiedSpeakers(row.speakerLabels, speakersByLabel),
        })),
      [rows, story, speakersByLabel],
    );

    // Empty until the creator inserts a boundary — a reel with no chapters
    // shows no chapter chrome, and the flat list stays the default (DS2).
    const chapterGroups = useMemo(
      () => deriveReelChapterGroups(rowViews, reel?.chapters),
      [rowViews, reel?.chapters],
    );

    // PLAT-11203 — the one read that decides which list is on screen (DS2), so
    // the rendering, the search's count and its walk can never disagree about
    // it. The groups above are derived either way: `chapterLayout` is built off
    // them, and every row and gap reads which chapter a drop lands in from that
    // map (DS1).
    const showChapters = chapterGroups.length > 0 && !areChaptersHidden;

    // PLAT-11074 — the utility bar's search. The query only ever narrows what
    // is rendered; the reel itself is untouched (DE4).
    const [searchQuery, setSearchQuery] = useState('');
    const query = normalizeReelQuery(searchQuery);
    const isFiltering = query.length > 0;

    const filteredRowViews = useMemo(
      () =>
        query
          ? rowViews.filter(view => reelRowMatches(view.row, query))
          : rowViews,
      [rowViews, query],
    );

    // A chapter whose heading matches keeps every Section under it; otherwise
    // the group keeps the Sections that match and drops out when none do (DS6).
    const filteredChapterGroups = useMemo(() => {
      if (!query) return chapterGroups;
      return chapterGroups
        .map(group =>
          chapterDisplayTitle(group.chapter, group.ordinal)
            .toLowerCase()
            .includes(query)
            ? group
            : {
                ...group,
                rows: group.rows.filter(view =>
                  reelRowMatches(view.row, query),
                ),
              },
        )
        .filter(group => group.rows.length > 0);
    }, [chapterGroups, query]);

    const hasMatches = showChapters
      ? filteredChapterGroups.length > 0
      : filteredRowViews.length > 0;

    // The Sections left on screen, in playback order — what the search walks.
    const visibleRows = useMemo(
      () =>
        (showChapters
          ? filteredChapterGroups.flatMap(group => group.rows)
          : filteredRowViews
        ).map(view => view.row),
      [showChapters, filteredChapterGroups, filteredRowViews],
    );

    // PLAT-11074 — where the query was found, so the field can count the
    // matches and Enter can walk them the way the transcript's search does.
    const searchMatches = useReelSearchMatches(editorStore, visibleRows, query);

    // PLAT-11063 — one event per query the creator settled on, not one per
    // keystroke: the field filters live, so every letter of "interview" would
    // otherwise be its own search. `query_length` stands in for the query
    // itself, which is transcript text typed back at us (DS3).
    const matchCount = searchMatches.length;
    useEffect(() => {
      if (!query) return;
      const timer = setTimeout(() => {
        EventTracker.trackReelEditorSearched({
          clip_id: clip?.id,
          match_count: matchCount,
          query_length: query.length,
        });
      }, SEARCH_TRACK_SETTLE_MS);
      return () => clearTimeout(timer);
    }, [query, matchCount, clip?.id]);

    const [activeMatchIndex, setActiveMatchIndex] = useState(0);
    // Whether the creator has been taken to a match yet. A fresh query has
    // moved nothing, so the first Enter goes to match 1 rather than past it.
    const hasJumpedRef = useRef(false);

    useEffect(() => {
      setActiveMatchIndex(0);
      hasJumpedRef.current = false;
    }, [query]);

    /**
     * Puts the playhead on the next match `step` away, wrapping at either end —
     * so holding Enter down walks the reel in a circle rather than stopping at
     * the last one.
     */
    const goToMatch = useCallback(
      (step: 1 | -1) => {
        const total = searchMatches.length;
        if (total === 0) return;
        const index = hasJumpedRef.current
          ? (activeMatchIndex + step + total) % total
          : activeMatchIndex;
        hasJumpedRef.current = true;
        setActiveMatchIndex(index);
        // The same nudge a row click takes: a match on a Section's first word
        // sits exactly on the boundary, which belongs to the Section before it.
        seekTo(searchMatches[index].time + SECTION_SEEK_NUDGE_SECONDS);
      },
      [activeMatchIndex, searchMatches, seekTo],
    );

    const goToNextMatch = useCallback(() => goToMatch(1), [goToMatch]);
    const goToPreviousMatch = useCallback(() => goToMatch(-1), [goToMatch]);

    // Fetch every frame while the tab is still hidden, so opening the Reel
    // Editor shows a complete list rather than filling in row by row.
    usePreloadThumbnails(rowViews.map(view => view.thumbnailUrl));

    // Which chapter each row runs under, and where a chapter that owns nothing
    // sits in the row order — the layout the drop targets are built from
    // (PLAT-11075 DS1). Read off the unfiltered groups: a drag never runs while
    // a search is filtering the list (PLAT-11074 DS5).
    const chapterLayout = useMemo(() => {
      const byRow = new Map<
        string,
        { chapterId: string; isFirst: boolean; chapterIndex: number }
      >();
      const boundaries = new Map<string, number>();
      let index = 0;
      chapterGroups.forEach((group, chapterIndex) => {
        boundaries.set(group.chapter.id, index);
        group.rows.forEach((view, position) => {
          byRow.set(view.id, {
            chapterId: group.chapter.id,
            isFirst: position === 0,
            // PLAT-11056 — which chapter this row sits in, for the Chapter drag:
            // every zone reports the boundary above the chapter it belongs to
            // (DS2), and a row is a zone like any other.
            chapterIndex,
          });
          index += 1;
        });
      });
      return { byRow, boundaries };
    }, [chapterGroups]);

    // PLAT-11098 DS3 — a second boundary on one Section is not representable, so
    // the bar's item is disabled there rather than enabled and inert (which
    // `insertChapterInReel`'s own no-op would make it).
    //
    // Read off the **derived** layout, not `reel.chapters[].firstSectionId`. The
    // two disagree whenever a chapter's anchor has left the reel: the derivation
    // advances that boundary onto whichever Section now comes first (PLAT-11055
    // DS3) while the stored anchor still names the departed one. Trusting the
    // stored value there would offer an insert at a Section the list already shows
    // a heading above — and `insertChapterInReel`, which also checks the stored
    // anchors, would add a second owning chapter on that boundary for
    // `deriveReelChapterGroups` to silently drop ("first one wins"). A chapter in
    // the array that renders nowhere.
    const chapterDisabledReason =
      activeSectionId && chapterLayout.byRow.get(activeSectionId)?.isFirst
        ? `“${activeRow?.displayName}” already starts a chapter`
        : undefined;

    /**
     * PLAT-11056 — a Chapter moves as a block: its whole run of Sections
     * permuted in one command, so the gesture the creator made is one undo
     * entry (DS1).
     *
     * Nothing is written to the reel (DS6). Each Chapter is anchored to its own
     * first Section, which travels with it — so every boundary is still correct
     * once the run has moved, and the derivation re-sorts the Chapters into
     * their new order with the ordinals renumbering themselves.
     */
    const moveChapter = useCallback(
      (fromChapterIndex: number, toChapterIndex: number) => {
        const editor = editorStore?.editor as VideoEditor | undefined;
        const from = chapterGroups[fromChapterIndex];
        if (!editor || !from) return;

        // Past the last Chapter is the end of the reel; a Chapter that owns
        // nothing has no first row, so its boundary comes off the layout.
        const target = chapterGroups[toChapterIndex];
        const boundaryIndex = target
          ? (target.rows[0]?.row.index ??
            chapterLayout.boundaries.get(target.chapter.id) ??
            rows.length)
          : rows.length;

        // A Chapter that owns nothing has no run to permute — its place comes
        // from its anchor rather than from the timeline (DS7), so it moves by
        // re-anchoring behind whichever Section now precedes it (PLAT-11072).
        // `''` is the gap at the top of the reel.
        if (from.rows.length === 0) {
          adapter?.moveEmptyReelChapter(
            from.chapter.id,
            boundaryIndex > 0 ? (rows[boundaryIndex - 1]?.id ?? '') : '',
          );
          EventTracker.trackReelChapterMoved({
            clip_id: clip?.id,
            from_index: fromChapterIndex,
            to_index: toChapterIndex,
            section_count: liveRows.length,
          });
          return;
        }

        const fromIndex = from.rows[0].row.index;

        // The run is lifted out before it is re-inserted, so a boundary below
        // it moves up by the run's length.
        const toIndex =
          boundaryIndex > fromIndex
            ? boundaryIndex - from.rows.length
            : boundaryIndex;
        if (toIndex === fromIndex) return;

        // Route through the editor's commandX wrapper, never a raw `timeline.*`
        // mutator (CLAUDE.md).
        const moved = editor.commandMoveReelChapter(
          from.rows.map(view => view.id),
          toIndex,
        );
        if (!moved) return;

        EventTracker.trackReelChapterMoved({
          clip_id: clip?.id,
          from_index: fromChapterIndex,
          to_index: toChapterIndex,
          section_count: liveRows.length,
        });

        // Same reasoning as a Section drop: one discrete edit, so flush the
        // store's 300ms debounce rather than re-arming it, and after the next
        // frame so the reordered list paints before the renderer work.
        requestAnimationFrame(() => void videoSourceStore?.flushChanges());
      },
      [
        adapter,
        chapterGroups,
        chapterLayout,
        clip?.id,
        editorStore,
        liveRows.length,
        rows,
        videoSourceStore,
      ],
    );

    // Below the layout it reads: a Chapter drag resolves against the groups,
    // which are derived further up the render.
    const drag = useReelEditorDrag(moveSection, moveChapter);
    // Only a Section drag grows a strip under an empty chapter to drop into;
    // both kinds stand the insert gestures down, since a gap in flight already
    // means "drop here" (PLAT-11075 DS5).
    const isSectionDragActive = drag.draggingIndex !== null;
    const isDragActive = isSectionDragActive || drag.draggingChapterId !== null;

    // One gesture at a time (DS7): the same reason `InsertDivider` stands its
    // insert down mid-drag. Stable while nothing is being dragged, so a playhead
    // tick does not hand every memoized row a fresh callback.
    const openSectionMenu = useCallback(
      (sectionId: string, x: number, y: number) => {
        if (isDragActive) return;
        setSectionMenu({ sectionId, x, y });
      },
      [isDragActive],
    );

    // PLAT-11064 — the Chapter heading's menu, on the same one-gesture-at-a-time
    // rule: a drag in flight already means "drop here".
    const openChapterMenu = useCallback(
      (chapterId: string, x: number, y: number) => {
        if (isDragActive) return;
        setChapterMenu({ chapterId, x, y });
      },
      [isDragActive],
    );

    // A Section, preceded by the gap between it and the row above. Every gap in
    // the list is a drop target (PLAT-11075 DS5): HTML5 DnD only allows a drop
    // where `dragover` called `preventDefault`, so a rule that handled nothing
    // would be 13px the drag dies on. The rule takes the same target as the
    // row's own top edge, and the row is what draws the line either way.
    const renderSection = (
      view: (typeof rowViews)[number],
      withDivider: boolean,
    ) => {
      const context = chapterLayout.byRow.get(view.id);
      return (
        <React.Fragment key={view.id}>
          {withDivider && (
            <InsertDivider
              // A rule under a heading is that chapter's start, not the gap
              // between two Sections — and the list's own opening rule already
              // claims the plain name at the top.
              gap={
                context?.isFirst
                  ? `chapter-start-${view.row.index + 1}`
                  : `${view.row.index + 1}`
              }
              position={`before clip ${view.row.index + 1}`}
              tooltipId={REEL_EDITOR_TOOLTIP_ID}
              // Where this boundary falls in the finished reel. Read off
              // playback order, so a reorder renumbers every gap below it.
              timestamp={convertTimeToMMSS(view.row.startTime)}
              // While a drag is in flight the gap already means "drop here"
              // (PLAT-11053). The hairline stays put so the list does not shift
              // under the pointer; only the gesture steps aside.
              canInsert={!isDragActive}
              // A Section that already opens a chapter cannot open a second
              // (DS6) — but a clip can still go in that gap, so only the chapter
              // half of the menu stands down (PLAT-11068 DS3). Hidden headings
              // stand it down for the whole list, for the same reason
              // (PLAT-11203 DS5).
              canInsertChapter={!context?.isFirst && !areChaptersHidden}
              // This rule *is* the insertion line: one green line per drop,
              // drawn where the Section would land (DS12). It lights whether the
              // pointer is on the rule itself or on either row's near half —
              // all three report the same gap.
              isDropTarget={isGapDropTarget(
                drag.dropTarget,
                view.row.index,
                context?.chapterId,
                context?.isFirst,
              )}
              onInsertChapter={() => insertChapter(view.id, 'divider')}
              // Under a chapter heading the insert claims that chapter's start,
              // so the Section lands beneath the heading it was added below
              // (PLAT-11068 DS4).
              onAddClips={() =>
                openPickerAtGap(
                  view.row.index,
                  context?.isFirst ? context.chapterId : undefined,
                )
              }
              onDragOver={
                isFiltering
                  ? undefined
                  : e =>
                      drag.handleGapDragOver(
                        e,
                        view.row.index,
                        context?.chapterId,
                        context?.isFirst,
                        view.id,
                        'above',
                        context?.chapterIndex,
                      )
              }
              onDrop={isFiltering ? undefined : drag.handleDrop}
            />
          )}
          <ReelSectionRow
            row={view.row}
            thumbnailUrl={view.thumbnailUrl}
            speakers={view.speakers}
            // A row of the Chapter being dragged travels with it, so it dims
            // with the heading rather than staying behind at full strength.
            isDragging={
              drag.draggingIndex === view.row.index ||
              drag.draggingChapterId === context?.chapterId
            }
            isActive={view.id === activeSectionId}
            // True only on the row being played — false everywhere else, so
            // the other rows' memo holds while playback runs.
            isPlayingHere={
              view.id === activeSectionId && adapter?.isPlaying === true
            }
            isJustAdded={justAdded?.ids.has(view.id) ?? false}
            flashMs={justAdded?.ms}
            flashTone={justAdded?.tone}
            sourceNote={sourceNoteById.get(view.id)}
            tooltipId={REEL_EDITOR_TOOLTIP_ID}
            // Only the marked row is given one, so a tick inside a Section
            // repaints that row alone and every other row's memo still holds.
            progress={
              view.id === activeSectionId
                ? sectionProgressAt(view.row, time)
                : undefined
            }
            // Expanded rows get their words for the fold; the marked row
            // always gets its own, for the sub-line's karaoke and its
            // auto-opened fold — it is already the only row repainting per
            // tick (PLAT-11246 rework).
            words={
              view.id === activeSectionId
                ? activeWords
                : wordsBySection.get(view.id)
            }
            hasTranscript={(view.row.elementIds?.length ?? 0) > 0}
            isExpanded={expandedSectionIds.has(view.id)}
            onToggleExpanded={toggleSectionExpanded}
            onWordSeek={seekWordAndPlay}
            chapterId={context?.chapterId}
            isFirstInChapter={context?.isFirst}
            chapterIndex={context?.chapterIndex}
            onSelect={selectSection}
            // Straight to the time the tracker names: it is already inside the
            // Section, so the nudge a row click needs would only move it off
            // what the readout said.
            onScrub={seekTo}
            onPlayToggle={toggleSectionPlayback}
            onRename={renameSection}
            isRenameRequested={renamingSectionId === view.id}
            onRenameRequestHandled={clearRenamingSectionId}
            // PLAT-11059 — the generation shows where its result will land, and
            // a failure stays on the row rather than in the menu that fired it
            // (DS6). Booleans and a string, so the row's memo still holds.
            isGeneratingName={generatingNameIds.has(view.id)}
            nameError={nameErrors[view.id]}
            onContextMenu={openSectionMenu}
            hasOpenMenu={sectionMenu?.sectionId === view.id}
            // Dropped while a search is filtering the list (PLAT-11074 DS5): a
            // position measured against rows that are not on screen means
            // nothing. `ReelSectionRow` keys `draggable` off `onDragStart`, so
            // withholding the handlers is the whole of it.
            onDragStart={isFiltering ? undefined : drag.handleDragStart}
            onDragOver={isFiltering ? undefined : drag.handleRowDragOver}
            onDrop={isFiltering ? undefined : drag.handleDrop}
            onDragEnd={isFiltering ? undefined : drag.handleDragEnd}
          />
        </React.Fragment>
      );
    };

    // The list opens and closes on the same rule that runs between its rows, so
    // the first and last Sections are bounded like the ones in between.
    //
    // On a *chaptered* reel it does not open on one at all (PLAT-11075 DS11).
    // Chapter 1's heading already carries the top of the list: the rule under it
    // is the same gap — same insert position, same Section target — and a
    // Chapter dropped at boundary 0 lands there too. Two rules with a heading
    // between them, both meaning "the start of the reel", read as two drop
    // points for one place.
    //
    // Both rules stand down while a search filters the list: a boundary
    // "between" two Sections that are not adjacent in the reel is not the
    // boundary the creator would be pointing at (PLAT-11074 DS5).
    // PLAT-11203 — and a reel whose headings are hidden opens on one again: the
    // heading that was carrying the top of the list is not on screen (DS2).
    const openingRule = !isFiltering &&
      !showChapters &&
      rowViews.length > 0 && (
        <InsertDivider
          gap="1"
          position="before clip 1"
          tooltipId={REEL_EDITOR_TOOLTIP_ID}
          // The top of the reel is 0 by definition, whatever is under it.
          timestamp={convertTimeToMMSS(0)}
          canInsert={!isDragActive}
          // With no headings on screen there is no boundary to aim a `+` at
          // (PLAT-11203 DS5). Add Clip stays: a clip goes in a gap whatever the
          // list is showing.
          canInsertChapter={!areChaptersHidden}
          isDropTarget={isGapDropTarget(drag.dropTarget, 0)}
          onInsertChapter={() => insertChapter(rowViews[0].id, 'divider')}
          onAddClips={() => openPickerAtGap(0)}
          onDragOver={e =>
            drag.handleGapDragOver(
              e,
              0,
              undefined,
              false,
              rowViews[0].id,
              'above',
            )
          }
          onDrop={drag.handleDrop}
        />
      );

    // The closing one opens an empty chapter to add Sections into (DS7), and
    // takes a drop at the end of the reel — which is the end of the last
    // chapter, or *into* it when that chapter is one of the empty ones.
    const lastGroup = chapterGroups.at(-1);
    const lastRowView = rowViews[rowViews.length - 1];
    const closingRule = !isFiltering && rowViews.length > 0 && (
      <InsertDivider
        gap="end"
        position="at the end of the reel"
        tooltipId={REEL_EDITOR_TOOLTIP_ID}
        // The end of the last Section, which is how long the reel runs.
        timestamp={convertTimeToMMSS(lastRowView.row.endTime)}
        canInsert={!isDragActive}
        canInsertChapter={!holderGaps.has(lastRowView.id) && !areChaptersHidden}
        onInsertChapter={() => appendChapter(lastRowView.id)}
        // An insert at the end anchors a trailing holder chapter onto it, which
        // `addClips` handles off the index alone (PLAT-11055 DS7).
        onAddClips={() => openPickerAtGap(rowViews.length)}
        // PLAT-11056 — and the one place a Chapter can land past every other:
        // the end of the reel. The rule draws that line itself, since there is
        // no heading below it to carry one.
        isDropTarget={
          (chapterGroups.length > 0 &&
            drag.chapterDropTarget?.index === chapterGroups.length) ||
          isGapDropTarget(
            drag.dropTarget,
            rowViews.length,
            lastGroup?.chapter.id,
            lastGroup?.rows.length === 0,
          )
        }
        onDragOver={e =>
          drag.handleGapDragOver(
            e,
            rowViews.length,
            lastGroup?.chapter.id,
            lastGroup?.rows.length === 0,
            lastRowView.id,
            'below',
            chapterGroups.length,
          )
        }
        onDrop={drag.handleDrop}
      />
    );

    return (
      <Container data-testid="reel-editor-panel">
        <Header>
          {/* PLAT-11074 — the controls take the row the title had (DS1); the
              summary underneath is information, so it stays. Undo and redo are
              the clip's own history, the one the Transcript tab reaches (DS2).
              Add Clips rides in the same bar: it is a control, and a control
              on its own row below the summary read as a second header. */}
          <ReelEditorUtilityBar
            query={searchQuery}
            onQueryChange={setSearchQuery}
            matchCount={searchMatches.length}
            activeMatchIndex={activeMatchIndex}
            onNextMatch={goToNextMatch}
            onPreviousMatch={goToPreviousMatch}
            canUndo={adapter?.canUndo ?? false}
            canRedo={adapter?.canRedo ?? false}
            hotkeys={isVisible}
            onUndo={() => {
              EventTracker.trackReelEditorUndone({ clip_id: clip?.id });
              adapter?.undo();
            }}
            onRedo={() => {
              EventTracker.trackReelEditorRedone({ clip_id: clip?.id });
              adapter?.redo();
            }}
            onAddClips={
              isLoading
                ? undefined
                : () => {
                    setPickerSectionId(null);
                    setPickerGap(null);
                    setIsPickerOpen(true);
                  }
            }
            // PLAT-11098 — the gap's own Add Chapter, reached from the bar and
            // aimed at the playhead's Section (DS1). No new write: `insertChapter`
            // already claims the reel's opening boundary when this is the first
            // (PLAT-11055 DS3), so the bar is simply a second caller (DS4).
            // PLAT-11203 DS4 — and it shows the chapters first: a boundary
            // written where the creator cannot see it is CLAUDE.md pitfall 9's
            // failure mode reached by a different road.
            onAddChapter={
              isLoading || !activeSectionId
                ? undefined
                : () => {
                    setAreChaptersHidden(false);
                    insertChapter(activeSectionId, 'playhead');
                  }
            }
            chapterDestination={activeRow?.displayName}
            chapterDisabledReason={chapterDisabledReason}
            // PLAT-11099 — the second item in the same menu. Absent callback,
            // absent item: with no Dato client there is no prompt record to
            // resolve, the convention every AI affordance in this panel follows.
            onAutoChapter={
              isLoading || !datoClientStore
                ? undefined
                : () => {
                    // Reveals for the same reason Add Chapter does (PLAT-11203
                    // DS4) — the run's whole output is chapters.
                    setAreChaptersHidden(false);
                    setIsAutoChapterOpen(true);
                  }
            }
            autoChapterDisabledReason={
              isChapterizing
                ? 'Already grouping this reel'
                : liveRows.length === 0
                  ? 'This reel has no clips to group'
                  : undefined
            }
            // PLAT-11203 / PLAT-11204 — both are chapter-set actions, so both
            // are absent on a reel that has no chapters (DS6 / DS5): nothing to
            // hide and nothing to remove.
            onToggleChaptersHidden={
              isLoading || !hasChapters ? undefined : toggleChaptersHidden
            }
            areChaptersHidden={areChaptersHidden}
            onRemoveAllChapters={
              isLoading || !hasChapters
                ? undefined
                : () => setIsRemoveAllOpen(true)
            }
            tooltipId={REEL_EDITOR_TOOLTIP_ID}
          />
          {/* Nothing here while the reel loads: the list below already says so,
              and two "Loading…"s one under the other read as two things
              loading. The slot keeps its height so the list does not jump when
              the summary arrives. */}
          <Subtitle data-testid="reel-editor-summary">
            {isLoading
              ? null
              : reelSummary(rows, speakersByLabel, {
                  chapterCount: chapterGroups.length,
                })}
          </Subtitle>
        </Header>

        {/* The face pile tags its avatars with this shared id but renders no
            Tooltip of its own; without an instance here, hovering a face in
            the Reel Editor would show nothing. Modal style — higher z-index. */}
        <Tooltip
          id={SPEAKER_FACE_PILE_TOOLTIP_ID}
          style={MODAL_TOOLTIP_STYLE}
        />
        <Tooltip id={REEL_EDITOR_TOOLTIP_ID} style={MODAL_TOOLTIP_STYLE} />

        {isLoading ? (
          <Status data-testid="reel-editor-loading">Loading…</Status>
        ) : (
          <List
            ref={listRef}
            onDragLeave={e => {
              // dragleave bubbles up from the rows as the pointer crosses
              // between them; clearing on those would blink the insertion line
              // off and on for the whole drag. Only a pointer that has left the
              // list itself counts.
              if (e.currentTarget.contains(e.relatedTarget as Node | null)) {
                return;
              }
              drag.handleDragLeave();
            }}
          >
            {openingRule}
            {/* Only while a query is narrowing the list. Without the guard a
                reel with no Sections at all — which DE15 says cannot happen,
                but which this line is what happens if it ever does — reads
                "No clips match “”". */}
            {isFiltering && !hasMatches && (
              <Status data-testid="reel-editor-no-matches">
                No clips match “{searchQuery.trim()}”
              </Status>
            )}
            {showChapters
              ? filteredChapterGroups.map((group, index) => {
                  const firstRow = group.rows[0];
                  const lastRow = group.rows[group.rows.length - 1];
                  // Where this chapter starts in the row order — its first
                  // Section, or for one that owns nothing the gap it holds.
                  const startIndex =
                    firstRow?.row.index ??
                    chapterLayout.boundaries.get(group.chapter.id) ??
                    0;
                  // PLAT-11056 — folded away by the creator, and stored so it
                  // comes back folded (DS3). A search opens it for the duration
                  // of the query: a chapter that survived the filter has
                  // matches to show, and hiding them would read as no result.
                  const isCollapsed =
                    !!group.chapter.collapsed &&
                    !isFiltering &&
                    group.chapter.id !== transientOpenChapterId;
                  return (
                    <React.Fragment key={group.chapter.id}>
                      <ReelChapterRow
                        chapter={group.chapter}
                        ordinal={group.ordinal}
                        sectionCount={group.rows.length}
                        // The reel's own summary line, scoped to this chapter —
                        // one helper, so the two always read the same way. Over
                        // the rows the group is *showing*, so it describes the
                        // Sections on screen even under a search filter.
                        summary={reelSummary(
                          group.rows.map(view => view.row),
                          speakersByLabel,
                          { chapterOrdinal: group.ordinal },
                        )}
                        onRename={renameChapter}
                        isRenameRequested={
                          renamingChapterId === group.chapter.id
                        }
                        onRenameRequestHandled={clearRenamingChapterId}
                        // PLAT-11064 — the heading reports a right-click, and
                        // its kebab reports a click, and it keeps no menu state
                        // of its own; the panel owns which heading is open, so a
                        // drag never races it. Passed whether or not there is a
                        // Dato client: Remove lives on this menu now, so a
                        // heading without one still has something to open.
                        onContextMenu={openChapterMenu}
                        hasOpenMenu={
                          chapterMenu?.chapterId === group.chapter.id
                        }
                        isGeneratingName={generatingChapterIds.has(
                          group.chapter.id,
                        )}
                        nameError={chapterNameErrors[group.chapter.id]}
                        // Derived, never stored (DS3): what it holds now against
                        // what its title was generated from.
                        isStale={isChapterTitleStale(
                          titledFrom[group.chapter.id],
                          group.rows.map(view => view.row.id),
                        )}
                        // PLAT-11056 — the chevron is one control doing two
                        // jobs (DS5): click folds this chapter away, drag moves
                        // it. Both stand down while a search filters the list,
                        // where a position means nothing (PLAT-11074 DS5).
                        collapsed={isCollapsed}
                        onToggleCollapse={toggleChapterCollapsed}
                        isDragging={drag.draggingChapterId === group.chapter.id}
                        // Keyed off the boundary, not off a chapter id: the
                        // rule that *closes* a chapter stands for the boundary
                        // above the next one, so the line has to draw on that
                        // chapter's heading rather than on the one whose zone
                        // the pointer is technically in.
                        isChapterDropTarget={
                          drag.chapterDropTarget?.index === index
                        }
                        onDragStart={
                          isFiltering
                            ? undefined
                            : e =>
                                drag.handleChapterDragStart(
                                  e,
                                  group.chapter.id,
                                  index,
                                )
                        }
                        onDragEnd={isFiltering ? undefined : drag.handleDragEnd}
                        // The heading stands for this chapter's start (DS1) —
                        // the same gap as its first row's top edge, so both
                        // draw the same line. An empty chapter has no row to
                        // draw on, so its own strip lights up instead; a folded
                        // one has none showing either, and lights up itself.
                        isDragActive={isSectionDragActive}
                        // Only an *empty* chapter lights itself: it has neither
                        // rows nor a closing rule of its own, so its strip is
                        // the only thing in that gap. A folded one keeps its
                        // rows' place in the list, so the rule under it is the
                        // line — one per drop, never two (DS12).
                        isDropTarget={
                          group.rows.length === 0 &&
                          drag.dropTarget?.chapterId === group.chapter.id
                        }
                        onDragOver={
                          isFiltering
                            ? undefined
                            : e =>
                                drag.handleGapDragOver(
                                  e,
                                  // Folded, the one unambiguous reading of
                                  // "into this chapter" is at its end — there
                                  // are no rows on screen to aim between (DS4).
                                  isCollapsed
                                    ? startIndex + group.rows.length
                                    : startIndex,
                                  group.chapter.id,
                                  !isCollapsed,
                                  isCollapsed ? undefined : firstRow?.id,
                                  isCollapsed || !firstRow
                                    ? undefined
                                    : 'above',
                                  index,
                                )
                        }
                        onDrop={isFiltering ? undefined : drag.handleDrop}
                      />
                      {/* Unmounted rather than hidden, so a long reel stays
                          cheap to render while its other Chapters are folded. */}
                      {!isCollapsed &&
                        group.rows.map(view =>
                          // Every Section is bounded above, the first one under
                          // a heading included: there the rule is the chapter's
                          // own start — a drop target, with no `+` on it, since
                          // the Section below already opens a chapter (DS6).
                          renderSection(view, !isFiltering),
                        )}
                      {/* Closes the chapter, so its last Section is bounded the
                          way every other row is instead of running straight
                          into the next heading. Its `+` opens an empty chapter
                          in that gap — the one thing insertable there, since a
                          boundary at the Section below is the chapter already
                          starting there (DE12). The final chapter is closed by
                          `closingRule`, which does the same. */}
                      {!isFiltering &&
                        lastRow &&
                        index < filteredChapterGroups.length - 1 && (
                          <InsertDivider
                            gap={`chapter-${group.ordinal}-end`}
                            position={`after chapter ${group.ordinal}`}
                            tooltipId={REEL_EDITOR_TOOLTIP_ID}
                            // The chapter's last Section ends where the next
                            // chapter's first begins — one boundary, one time.
                            timestamp={convertTimeToMMSS(lastRow.row.endTime)}
                            canInsert={!isDragActive}
                            canInsertChapter={!holderGaps.has(lastRow.id)}
                            // Lit for a Section landing at the end of this
                            // chapter, and for a Chapter landing between the two
                            // (DS12) — the rule is the line either way.
                            isDropTarget={
                              drag.chapterDropTarget?.index === index + 1 ||
                              isGapDropTarget(
                                drag.dropTarget,
                                lastRow.row.index + 1,
                                group.chapter.id,
                                false,
                              )
                            }
                            onInsertChapter={() => appendChapter(lastRow.id)}
                            // Ahead of the next chapter's boundary, so the
                            // Section joins the chapter this rule closes — no
                            // chapter write at all (PLAT-11068 DS4).
                            onAddClips={() =>
                              openPickerAtGap(lastRow.row.index + 1)
                            }
                            onDragOver={e =>
                              drag.handleGapDragOver(
                                e,
                                lastRow.row.index + 1,
                                group.chapter.id,
                                false,
                                lastRow.id,
                                'below',
                                // A Section dropped here joins the chapter this
                                // rule closes; a *Chapter* dropped here lands
                                // between the two, which is the boundary above
                                // the next one — hence `index + 1`, the same
                                // one-past reading the closing rule of the list
                                // takes. The two halves of this zone are not
                                // the same boundary (PLAT-11056 DS2).
                                index + 1,
                              )
                            }
                            onDrop={drag.handleDrop}
                          />
                        )}
                    </React.Fragment>
                  );
                })
              : filteredRowViews.map((view, index) =>
                  renderSection(view, !isFiltering && index > 0),
                )}
            {closingRule}
          </List>
        )}

        {/* PLAT-11099 — where the run is specified, and the confirmation step
            for a reel that already has chapters (DS7). Mounted only while open,
            so a dismissed dialog carries no form state into the next one. */}
        {isAutoChapterOpen && (
          <AutoChapterOverlay
            isOpen
            onClose={closeAutoChapter}
            onRun={options => void runChapterization(options)}
            sectionCount={liveRows.length}
            existingChapterCount={chapterCount}
            rows={autoChapterRows}
            isRunning={isChapterizing}
            error={chapterizeError}
          />
        )}

        {/* PLAT-11204 — the confirm, and the whole of the protection this
            action has (DS2): a chapter is a `clipJson.reel` write, so the
            panel's undo cannot take it back. The count is in the message and the
            names are not: the list behind this dialog is showing them. */}
        {isRemoveAllOpen && (
          <ConfirmModal
            closeModal={() => setIsRemoveAllOpen(false)}
            onCancel={() => setIsRemoveAllOpen(false)}
            onConfirm={removeAllChapters}
            title="Remove All Chapters?"
            message={`This reel's ${chapterCount} ${
              chapterCount === 1 ? 'chapter' : 'chapters'
            } will be removed, names and all. The sections, their order and their names stay as they are — and this cannot be undone.`}
            confirmText="Remove All"
            cancelText="Cancel"
          />
        )}

        {/* PLAT-11059 DS2 / PLAT-11064 DS5 — the ask before a generated name
            lands on top of one the creator typed, and where they say what they
            want it to say. Cancel keeps the typed name and nothing is requested;
            the generation only starts once it is asked for here. */}
        {nameDialogTarget && (
          <ReelNameGenerationDialog
            kind={nameDialogTarget.kind}
            currentName={nameDialogTarget.currentName}
            onClose={() => setNameDialogTarget(null)}
            onGenerate={runNameGeneration}
          />
        )}

        {chapterMenu && (
          <ReelChapterContextMenu
            target={chapterMenu}
            onClose={closeChapterMenu}
            // The heading's own pencil, opened from the menu: the panel only
            // says which heading was asked, and that heading opens its field.
            onRename={setRenamingChapterId}
            // Absent callback, absent item — with no Dato client there is no
            // prompt record to resolve, which would leave a menu of nothing.
            onGenerateName={
              datoClientStore
                ? chapterId =>
                    void generateChapterName(chapterId, {
                      confirmOverUserTitle: true,
                    })
                : undefined
            }
            isGeneratingName={generatingChapterIds.has(chapterMenu.chapterId)}
            onRemove={removeChapter}
            // The opening chapter cannot go while others remain — the Sections
            // above the next boundary would be left outside every chapter
            // (DS3). Alone it can, and that returns the reel to the flat list
            // (DS4). Read off the ordinal, not the rendered position: a filter
            // can hide the opening chapter without making the next one it.
            canRemove={
              (chapterGroups.find(
                group => group.chapter.id === chapterMenu.chapterId,
              )?.ordinal ?? 1) > 1 || chapterGroups.length === 1
            }
            removeDisabledReason="The first chapter cannot be removed while others remain"
          />
        )}

        {sectionMenu && (
          <ReelSectionContextMenu
            target={sectionMenu}
            onClose={closeSectionMenu}
            // Slot 0 — the row's own title field, opened from the menu: the
            // panel only says which row was asked, and that row opens it.
            onRename={setRenamingSectionId}
            // Slot 1 — names the Section from what is said in it (PLAT-11059).
            // Absent callback, absent item, the convention every slot on this
            // menu follows: with no Dato client there is no prompt record to
            // resolve, so there is nothing the item could do.
            onGenerateName={datoClientStore ? generateSectionName : undefined}
            isGeneratingName={generatingNameIds.has(sectionMenu.sectionId)}
            // Slot 1. Absent host callback, absent item — a clip preview with
            // no transcript tab has nowhere to send them (PLAT-11061).
            onEditTranscript={
              onEditSectionTranscript ? editSectionTranscript : undefined
            }
            // Closes the menu and opens the picker on that Section, frozen at
            // this click (PLAT-11071 DS6).
            onReplace={openReplacePicker}
            // PLAT-11072 — slot 3, and the flag that decides its two labels and
            // greys out the two slots above it on a Section that is off (DS7).
            onToggleEnabled={toggleSectionEnabled}
            isEnabled={menuRow?.enabled ?? true}
            // A reel has to keep one Section playing, for the reason it has to
            // keep one at all (DS5).
            canDisable={liveRows.length > 1}
            disableDisabledReason="A reel needs at least one clip playing"
            onRemove={removeSection}
            // A reel of zero Sections is not a state anything downstream
            // expects, and DE15 makes a one-Section reel the common case — so
            // the item is disabled rather than dropped (DS10). Counted over the
            // Sections that are on: a row that is off can always go, and taking
            // one does not shorten the reel.
            canRemove={menuRow?.enabled === false || liveRows.length > 1}
            removeDisabledReason="A reel needs at least one clip"
          />
        )}

        {isPickerOpen && (
          <AddClipsPicker
            clips={candidateClips}
            // "Already in the reel" means playing in it: a clip whose passage
            // has been turned off is offered again like any other (PLAT-11072).
            sections={liveRows}
            destination={destination}
            story={story}
            // One picker, three callers: the utility bar, a gap, and a Section
            // being replaced. Only the last is a swap (PLAT-11071 DS2).
            selectionMode={pickerSectionId ? 'single' : 'multi'}
            // PLAT-11096 — the filters read what the panel already holds: the
            // same resolved speakers the Section rows' face piles use, and the
            // story's own topics. No second fetch (DS4).
            speakersByLabel={speakersByLabel}
            topics={topicsForFilter}
            topicSpans={topicSpans}
            // PLAT-11109 — the words behind the previewed row's scrub readout.
            sourceElements={sourceElements}
            onClose={closePicker}
            onConfirm={pickerSectionId ? replaceSection : addClips}
          />
        )}
      </Container>
    );
  },
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  height: 100%;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;
`;

/* Starts on the thumbnail column — the rows' own 10px, the grip's 10px handle
   column and the 10px gap after it — so it sits directly above each chapter
   heading's `n clips · mm:ss` and the column reads straight down. The list
   carries no left padding of its own, so this line and the rows inside it share
   one origin. */
const Subtitle = styled.span`
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
  /* Held whether or not there is a summary in it yet. */
  min-height: var(--leading-text-xs);
  color: var(--text-muted);
  padding-left: calc(10px + 10px + 10px);
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  /* No gap of its own: the hairline between two Sections is what separates
     them now, and it carries its own breathing room. */
  overflow-y: auto;
  min-height: 0;
  /* Keep the wheel in this list: without it, a scroll that reaches either end —
     or one over a reel too short to scroll at all — chains out to the Content
     Studio page behind the modal, which then moves under the creator. */
  overscroll-behavior: contain;
  /* Hold the scrollbar's 8px whether or not the reel scrolls, so the rows keep
     one right edge instead of shifting sideways the moment a reel grows past
     the panel. */
  scrollbar-gutter: stable;
`;

const Status = styled.div`
  font-size: var(--text-sm);
  color: var(--text-muted);
  padding: 10px 12px;
`;
